import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFileDrop } from '@/hooks/useFileDrop';
import { rtlContentProps } from '@/utils/rtl';
import { decodeEntities } from '@/utils/decodeEntities';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/Toast';
import { useLocation } from 'react-router-dom';
import { buildPageContext } from '@/lib/page-context';
import { trackView, trackClick, trackEngage, trackVideo, track, flushEvents } from '@/lib/events';
import { useAIAssistantContext } from '@/contexts/AIAssistantContext';
import {
  Send,
  Heart,
  MessageSquare,
  Repeat2,
  Link2,
  ImagePlus,
  Library,
  Hash,
  Globe,
  MoreHorizontal,
  Loader2,
  Trash2,
  Pin,
  ExternalLink,
  ChevronDown,
  X,
  AtSign,
  Pencil,
  Check,
  Play,
  SmilePlus,
  Sparkles,
  DollarSign,
  Lock,
  BarChart2,
  Calendar,
  Clock,
  MapPin,
  Users,
  CheckCircle2,
  Wand2,
  Share2,
  Copy,
  Award,
  Flag,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  Mic,
  Camera,
  ShoppingBag,
  GraduationCap,
  Flame,
  Trophy,
} from 'lucide-react';
import MediaStudioPanel from '@/components/MediaStudioPanel';
import MediaLibraryPicker, { PickedAsset } from '@/components/feed/MediaLibraryPicker';
import FeedStoryRing from '@/components/scoops/FeedStoryRing';
import FeedReasonBar from '@/components/scoops/FeedReasonBar';
import TargetedPromptCard from '@/components/home/TargetedPromptCard';
import FirstPostPrompt, { markUserHasPosted } from '@/components/onboarding/FirstPostPrompt';
import GifPicker from '@/components/scoops/GifPicker';
import OutcomeReactions from '@/components/scoops/OutcomeReactions';
import ImageEditor from '@/components/ImageEditor';
import ContentTemplates from '@/components/ContentTemplates';
import DateTimePicker from '@/components/DateTimePicker';
import ImageCarousel from '@/components/ImageCarousel';
import { isAiEditedPost } from '@/lib/ai-edited';
import SocialProofNudge from '@/components/feed/SocialProofNudge';
import DailyPromptCard from '@/components/feed/DailyPromptCard';
import DraftNudgeBanner from '@/components/feed/DraftNudgeBanner';
import PostSuggestionBanner from '@/components/feed/PostSuggestionBanner';
import PostROIBadge from '@/components/scoops/PostROIBadge';
import ThreadedComments from '@/components/scoops/ThreadedComments';
import CameraCapture from '@/components/CameraCapture';
import { BadgeRow } from '@/components/BadgeDisplay';
import { FeedSkeleton } from '@/components/ui/Skeleton';
import { registerTrackedLinks, trackedUrl } from '@/utils/trackedUrl';
import PageTransition from '@/components/ui/PageTransition';
import { useDesignV2 } from '@/hooks/useDesignV2';
import StoriesRow from '@/components/chat/StoriesRow';
import Link from '@/components/LocaleLink';
import { useLocaleNavigate } from '@/hooks/useLocaleNavigate';
import { fmtShopPrice } from '@/types/shop';
import SocialProofBadge from '@/components/social/SocialProofBadge';
import VerifiedBadge from '@/components/badges/VerifiedBadge';
import { formatDate, formatDateTime, formatTime as formatTime24 } from '@/lib/dates';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const getAuthToken = () => {
  if (typeof window !== 'undefined') return localStorage.getItem('authToken');
  return null;
};

const REPORT_CATEGORIES = [
  { code: 'spam', labelKey: 'spam', descKey: 'spamDesc' },
  { code: 'nudity', labelKey: 'nudity', descKey: 'nudityDesc' },
  { code: 'hate', labelKey: 'hate', descKey: 'hateDesc' },
  { code: 'harassment', labelKey: 'harassment', descKey: 'harassmentDesc' },
  { code: 'violence', labelKey: 'violence', descKey: 'violenceDesc' },
  { code: 'terrorism', labelKey: 'terrorism', descKey: 'terrorismDesc' },
  { code: 'self_harm', labelKey: 'selfHarm', descKey: 'selfHarmDesc' },
  { code: 'misinfo', labelKey: 'misinfo', descKey: 'misinfoDesc' },
  { code: 'csam', labelKey: 'csam', descKey: 'csamDesc' },
  { code: 'illegal', labelKey: 'illegal', descKey: 'illegalDesc' },
  { code: 'impersonation', labelKey: 'impersonation', descKey: 'impersonationDesc' },
  { code: 'ip', labelKey: 'ip', descKey: 'ipDesc' },
  { code: 'other', labelKey: 'other', descKey: 'otherDesc' },
] as const;

type ReportCategory = typeof REPORT_CATEGORIES[number]['code'];

interface VideoEmbed {
  platform: 'youtube' | 'vimeo';
  id: string;
  embedUrl: string;
  thumbnailUrl: string;
}

function detectVideoUrl(url: string): VideoEmbed | null {
  if (!url) return null;
  let u: URL;
  try { u = new URL(url.startsWith('http') ? url : `https://${url}`); } catch { return null; }

  // YouTube
  if (u.hostname.match(/(?:www\.)?youtube\.com/)) {
    const videoId = u.searchParams.get('v') || u.pathname.match(/\/(?:embed|shorts)\/([^/?]+)/)?.[1];
    if (videoId) return { platform: 'youtube', id: videoId, embedUrl: `https://www.youtube.com/embed/${videoId}`, thumbnailUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` };
  }
  if (u.hostname === 'youtu.be') {
    const videoId = u.pathname.slice(1).split('/')[0];
    if (videoId) return { platform: 'youtube', id: videoId, embedUrl: `https://www.youtube.com/embed/${videoId}`, thumbnailUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` };
  }

  // Vimeo
  if (u.hostname.match(/(?:www\.)?vimeo\.com/)) {
    const videoId = u.pathname.match(/^\/(\d+)/)?.[1];
    if (videoId) return { platform: 'vimeo', id: videoId, embedUrl: `https://player.vimeo.com/video/${videoId}`, thumbnailUrl: '' };
  }

  return null;
}

interface MentionUser {
  id: string;
  displayName: string;
  username: string;
  avatarUrl: string | null;
}

interface MentionData {
  userId: string;
  username: string;
  displayName: string;
}

function MentionDropdown({
  query,
  position,
  onSelect,
  onClose,
}: {
  query: string;
  position: { top: number; left: number };
  onSelect: (user: MentionUser) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [users, setUsers] = useState<MentionUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (query.length < 1) { setUsers([]); return; }
    setLoading(true);
    const token = getAuthToken();
    fetch(`${API_URL}/users/search?q=${encodeURIComponent(query)}&limit=6`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(data => { setUsers(data.users || []); setSelectedIndex(0); })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(i => (i + 1) % Math.max(users.length, 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(i => (i - 1 + users.length) % Math.max(users.length, 1)); }
      if ((e.key === 'Enter' || e.key === 'Tab') && users.length > 0) { e.preventDefault(); onSelect(users[selectedIndex]); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [users, selectedIndex, onSelect, onClose]);

  if (users.length === 0 && !loading) return null;

  return (
    <div
      ref={ref}
      className="absolute z-50 w-64 rounded-xl bg-[#16161e] border border-white/10 shadow-2xl overflow-hidden"
      style={{ top: position.top, left: position.left }}
    >
      {loading ? (
        <div className="flex items-center gap-2 px-3 py-2.5">
          <Loader2 size={12} className="animate-spin text-[#9a9aac]" />
          <span className="text-xs text-[#9a9aac]">{t('dashboardMain.mention.searching', "Searching...")}</span>
        </div>
      ) : (
        users.map((user, i) => (
          <button
            key={user.id}
            onClick={() => onSelect(user)}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
              i === selectedIndex ? 'bg-iris-600/20' : 'hover:bg-white/5'
            }`}
          >
            <div className="w-6 h-6 rounded-full bg-linear-to-br/srgb from-iris-500/30 to-iris-500/30 flex items-center justify-center text-[13px] text-white font-medium overflow-hidden shrink-0">
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                user.displayName.charAt(0).toUpperCase()
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs text-white font-medium truncate">{user.displayName}</div>
              {user.username && <div className="text-[12px] text-[#9a9aac] truncate">@{user.username}</div>}
            </div>
          </button>
        ))
      )}
    </div>
  );
}

interface ScoopPost {
  id: string;
  user_id: string;
  content_type: string;
  url: string | null;
  tracked_url?: string;
  tracked_links?: Record<string, string>;
  title: string | null;
  description: string | null;
  image_url: string | null;
  media_urls: any[];
  metadata: any;
  hub_id: string | null;
  is_personal: boolean;
  tags: string[];
  like_count: number;
  reply_count: number;
  reshare_count: number;
  view_count: number;
  scoop_count: number;
  is_pinned: boolean;
  created_at: string;
  edited_at: string | null;
  status: string;
  scheduled_at: string | null;
  author_display_name?: string | null;
  author_name: string | null;
  author_username: string | null;
  author_avatar: string | null;
  author_is_official?: boolean;
  author_is_verified?: boolean;
  author_account_type?: string;
  hub_name: string | null;
  hub_icon: string | null;
  hub_type: string | null;
  viewer_liked: boolean;
  viewer_voted: boolean;
  is_gated: boolean;
  gate_tier_id: string | null;
  viewer_has_access: boolean;
  reshared_scoop?: ScoopPost | null;
  certificate?: {
    is_original?: boolean;
    first_badge?: boolean;
  } | null;
  // Agentic Phase 2 — provenance + explainable ranking from the For-You feed.
  generation_source?: string | null;
  agent_name?: string | null;
  viewer_saved?: boolean;
  _ranking?: {
    score?: number;
    behavioral?: {multiplier:number; baseScore:number; source:string; version:string; components:Record<string,number>};
    reasons?: string[];
    suggestedAction?: 'reply' | 'reshare' | 'save' | null;
  } | null;
}

interface ScoopReaction {
  emoji: string;
  count: number;
  viewerReacted: boolean;
}

interface PollData {
  options: string[];
  option_images?: (string | null)[] | null;
  votes_by_option: number[];
  total_votes: number;
  viewer_votes: number[];
  expired: boolean;
  expires_at: string | null;
  multi_vote: boolean;
}

interface EventData {
  starts_at: string;
  ends_at: string | null;
  location_url: string | null;
  rsvp_count: number;
  viewer_rsvp: string | null;
}

type FeedSortMode = 'new' | 'hot' | 'trending';

interface HubTier {
  id: string;
  name: string;
  price_monthly: number;
  price_onetime: number;
  sort_order: number;
}

interface ReplyPost {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  author_display_name?: string | null;
  author_name: string | null;
  author_username: string | null;
  author_avatar: string | null;
  author_is_official?: boolean;
  author_is_verified?: boolean;
  is_answer?: boolean;
}

type FeedTab = 'for-you' | 'following' | 'friends' | 'discover' | 'agents';

export default function DashboardPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useLocaleNavigate();
  const { pathname } = useLocation();
  const { setPageContext } = useAIAssistantContext();
  const [scoops, setScoops] = useState<ScoopPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState<FeedTab>('for-you');
  // V2: trim feed banners/tabs to match docs/design/mocks/dashboard-feed.html.
  // Hides streak chip + DailyPromptCard, collapses tabs to For You / Following / Hubs.
  const designV2 = useDesignV2();
  const feedRankingV2 = useFeatureFlag('feed_ranking_v2');
  const [feedSort, setFeedSort] = useState<FeedSortMode>('new');
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const loadedReactionsRef = useRef<Set<string>>(new Set());
  // Infinite-scroll stop guards: track every scoop id we've shown (stable
  // dedup without reading state), and count consecutive pages that added
  // nothing new — so an all-duplicate / empty page run ends the scroll
  // instead of looping forever (the "stuck, shows no more" bug).
  const seenScoopIdsRef = useRef<Set<string>>(new Set());
  const dupePageCountRef = useRef(0);
  const forYouCursorRef = useRef<string | null>(null);
  // When the personalized For-You pool runs dry on a small/young platform the
  // feed must not dead-end. It continues from Discover, which applies the same
  // language rules, and a divider shows where the continuation starts.
  const forYouOverflowRef = useRef(false);
  const overflowOffsetRef = useRef(0);
  const overflowDividerPendingRef = useRef(false);
  const [overflowStartId, setOverflowStartId] = useState<string | null>(null);
  const fetchScoopsRef = useRef<(reset?: boolean) => Promise<void>>(async () => {});
  const feedRequestRef = useRef(0);
  const feedPendingRef = useRef(false);
  const [feedError, setFeedError] = useState('');
  const [scoopReactions, setScoopReactions] = useState<Record<string, ScoopReaction[]>>({});
  const [showReactionPicker, setShowReactionPicker] = useState<string | null>(null);
  const [userBadges, setUserBadges] = useState<Record<string, { badge_type: string; badge_data?: any }[]>>({});

  // ── Engagement tracking ──
  // All behavioral tracking goes through lib/events (the /events pipeline);
  // the server forwards qualified signals to interest scoring, so the old
  // parallel /engagement/track queue is gone.
  const viewTimersRef = useRef<Map<string, number>>(new Map());
  const sentViewsRef = useRef<Set<string>>(new Set());

  // One feed-view event per visit to the home feed
  useEffect(() => {
    trackView.feed('feed');
  }, []);

  // Qualified attention only while visible and foregrounded; a static card
  // can qualify without requiring the person to scroll it out of view.
  const observerRef = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    const visible = new Set<string>();
    const reads = new Set<string>();
    observerRef.current = new IntersectionObserver(entries=>{
      for(const entry of entries){
        const id=entry.target.getAttribute('data-scoop-id');
        if(!id)continue;
        if(entry.isIntersecting && entry.intersectionRatio>=.5 && document.visibilityState==='visible'){
          visible.add(id);if(!viewTimersRef.current.has(id))viewTimersRef.current.set(id,Date.now());
        } else {
          const start=viewTimersRef.current.get(id);
          const duration=start ? Date.now()-start : 0;
          if(document.visibilityState==='visible' && duration>=500 && duration<3000 && !sentViewsRef.current.has(id))
            track('skip.post',{post_id:id,surface:'feed',metadata:{visible_ms:duration}});
          visible.delete(id);viewTimersRef.current.delete(id);
        }
      }
    },{threshold:.5});
    const tick=window.setInterval(()=>{
      if(document.visibilityState!=='visible')return;
      for(const id of visible){
        const start=viewTimersRef.current.get(id);
        if(!start){viewTimersRef.current.set(id,Date.now());continue;}
        const duration=Date.now()-start;
        if(duration>=3000&&!sentViewsRef.current.has(id)){sentViewsRef.current.add(id);trackView.post(id,'feed',duration);}
        if(duration>=15000&&!reads.has(id)){reads.add(id);trackView.read(id,duration,'feed');}
      }
    },1000);
    const pause=()=>{viewTimersRef.current.clear();};
    document.addEventListener('visibilitychange',pause);
    return()=>{window.clearInterval(tick);observerRef.current?.disconnect();document.removeEventListener('visibilitychange',pause);};
  }, []);

  const handleFeedFeedback = async (postId:string,action:'hide'|'not_interested')=>{
    try {
      const res=await fetch(`${API_URL}/me/feed-feedback`,{method:'POST',
        headers:{Authorization:`Bearer ${getAuthToken()}`,'Content-Type':'application/json'},body:JSON.stringify({postId,action})});
      if(!res.ok)throw new Error('feedback');
      const result=await res.json();
      if(!result.learned)throw new Error('feedback');
      setScoops(rows=>rows.filter(row=>row.id!==postId));
    } catch { setFeedError(t('dashboardMain.feed.feedbackFailed', "Could not save your feedback. Please try again.")); }
  };
  const savingPosts=useRef(new Set<string>());
  const handleSave = async (postId:string)=>{
    if(savingPosts.current.has(postId))return;
    savingPosts.current.add(postId);
    try {
      const res=await fetch(`${API_URL}/scoops/${postId}/save`,{method:'POST',headers:{Authorization:`Bearer ${getAuthToken()}`}});
      if(!res.ok)throw new Error('save');
      const result=await res.json();
      setScoops(rows=>rows.map(row=>row.id===postId?{...row,viewer_saved:result.saved}:row));
      if(result.saved)trackEngage.save(postId,'feed');else trackEngage.unsave(postId,'feed');
    } catch { setFeedError(t('dashboardMain.feed.saveFailed', "Could not update your saved posts. Please try again.")); }
    finally { savingPosts.current.delete(postId); }
  };
  // Only count real playback while the card is qualified as visible. Seeking
  // cannot manufacture watch time, and looping cannot emit repeated completions.
  const videoWatch=useRef(new WeakMap<HTMLVideoElement,{last:number;position:number;watched:number;sent:boolean}>());
  const recordVideo=(event:React.SyntheticEvent<HTMLDivElement>,postId:string)=>{
    const video=event.target;
    if(!(video instanceof HTMLVideoElement))return;
    const now=performance.now();
    const prior=videoWatch.current.get(video)||{last:now,position:video.currentTime,watched:0,sent:false};
    const elapsed=(now-prior.last)/1000, advanced=video.currentTime-prior.position;
    if(document.visibilityState==='visible' && viewTimersRef.current.has(postId) && !video.seeking
      && elapsed>0 && elapsed<1.5 && advanced>0 && advanced<=elapsed*Math.max(1,video.playbackRate)+.2)
      prior.watched+=Math.min(advanced,elapsed);
    prior.last=now;prior.position=video.currentTime;
    if(!prior.sent && video.ended && Number.isFinite(video.duration) && prior.watched>=Math.max(3,video.duration*.9)){
      prior.sent=true;trackVideo.complete(postId,'feed',prior.watched*1000);
    }
    videoWatch.current.set(video,prior);
  };

  // Track link clicks on scoops
  const trackScoopClick = useCallback((scoopId: string) => {
    trackClick.post(scoopId, 'feed');
  }, []);

  // Composer state
  const [composerText, setComposerText] = useState('');
  const [composerUrl, setComposerUrl] = useState('');
  const [composerTags, setComposerTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);
  const [posting, setPosting] = useState(false);
  const [composerImages, setComposerImages] = useState<File[]>([]);
  const [composerImagePreviews, setComposerImagePreviews] = useState<string[]>([]);
  const [preUploadedUrls, setPreUploadedUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const [linkPreview, setLinkPreview] = useState<{ title?: string | null; description?: string | null; image?: string | null; site_name?: string | null; favicon?: string | null } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isGated, setIsGated] = useState(false);
  const [gateTierId, setGateTierId] = useState('');
  const [hubTiers, setHubTiers] = useState<HubTier[]>([]);
  const urlDebounceRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  // The "new post" action is owned by DashboardLayout (listens for the
  // 'soltik:compose' window event and for the ?compose=1 URL param for
  // external deep links). Home doesn't try to focus its inline composer
  // — that would scroll readers away from wherever they were in the feed.
  const [composerMentions, setComposerMentions] = useState<MentionData[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionPos, setMentionPos] = useState({ top: 0, left: 0 });
  const [postType, setPostType] = useState<'text' | 'poll' | 'event' | 'story' | 'status'>('text');
  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);
  const [pollOptionImages, setPollOptionImages] = useState<(string | null)[]>([null, null]);
  const [pollExpiresAt, setPollExpiresAt] = useState('');
  const [pollMultiVote, setPollMultiVote] = useState(false);
  const [eventStartsAt, setEventStartsAt] = useState('');
  const [eventEndsAt, setEventEndsAt] = useState('');
  const [eventLocationUrl, setEventLocationUrl] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);
  const [showMediaStudio, setShowMediaStudio] = useState(false);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [imageToEdit, setImageToEdit] = useState<{ file: File; index: number } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [storyHubId, setStoryHubId] = useState('');
  const [userHubs, setUserHubs] = useState<{ id: string; name: string }[]>([]);
  const [storyRefreshKey, setStoryRefreshKey] = useState(0);

  // Activity streak state
  const [streak, setStreak] = useState(0);
  const [freezesAvailable, setFreezesAvailable] = useState(0);
  const [freezeUsedToday, setFreezeUsedToday] = useState(false);
  const [newAchievements, setNewAchievements] = useState<string[]>([]);
  const [showAchievementToast, setShowAchievementToast] = useState(false);

  // AI Post Writer state
  const [showAIWriter, setShowAIWriter] = useState(false);
  const [aiWritePrompt, setAiWritePrompt] = useState('');
  const [aiWriteTone, setAiWriteTone] = useState<'casual' | 'professional' | 'fun'>('casual');
  const [aiWriting, setAiWriting] = useState(false);

  // Suggested channels state (for new users)
  const [suggestedChannels, setSuggestedChannels] = useState<{id: string; username: string; display_name: string; avatar_url: string | null; bio: string | null; follower_count: number; post_count: number; category: string; is_following: boolean}[]>([]);
  const [followingCount, setFollowingCount] = useState<number | null>(null);
  const [followedChannelIds, setFollowedChannelIds] = useState<Set<string>>(new Set());
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => {
    try { return localStorage.getItem('soltik_welcome_dismissed') === 'true'; } catch { return false; }
  });

  const handleAIWrite = async (prompt: string) => {
    if (!prompt.trim()) return;
    setAiWriting(true);
    setShowAIWriter(false);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/ai/write-post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ prompt: prompt.trim(), tone: aiWriteTone }),
      });
      const data = await res.json();
      if (data.success && data.generated) {
        setComposerText(data.generated.trim());
        composerRef.current?.focus();
      }
    } catch {
      // silently fail
    } finally {
      setAiWriting(false);
      setAiWritePrompt('');
    }
  };

  const handleInsertMedia = useCallback(async (url: string, attribution?: string) => {
    // Already-uploaded media (from Media Studio voice/screen recording)
    if (attribution === '__already_uploaded__') {
      const relUrl = url.includes('/capture/media/') ? url.slice(url.indexOf('/capture/media/')) : url;
      setPreUploadedUrls(prev => [...prev, relUrl]);
      const isAudio = url.includes('.webm') && !url.includes('video');
      setComposerImagePreviews(prev => [...prev, isAudio ? '__audio__' : url]);
      return;
    }
    if (composerImages.length + preUploadedUrls.length >= 4) return;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const ext = blob.type.includes('gif') ? 'gif' : blob.type.includes('png') ? 'png' : blob.type.includes('webm') ? 'webm' : blob.type.includes('mp4') ? 'mp4' : blob.type.includes('video') ? 'webm' : 'jpg';
      const file = new File([blob], `media-${Date.now()}.${ext}`, { type: blob.type });
      setComposerImages(prev => [...prev, file]);
      const reader = new FileReader();
      reader.onload = () => setComposerImagePreviews(prev => [...prev, reader.result as string]);
      reader.readAsDataURL(file);
      if (attribution) {
        setComposerText(prev => prev ? `${prev}\n${attribution}` : attribution);
      }
    } catch {
      setComposerImagePreviews(prev => [...prev, url]);
    }
  }, [composerImages.length, preUploadedUrls.length]);

  // Attach already-hosted media-library assets (no re-upload).
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const insertLibraryAssets = useCallback((assets: PickedAsset[]) => {
    setPreUploadedUrls(prevUrls => {
      const slots = Math.max(0, 4 - (composerImages.length + prevUrls.length));
      const take = assets.slice(0, slots);
      if (take.length === 0) return prevUrls;
      const previews = take.map(a =>
        a.mime_type.startsWith('audio/') ? '__audio__' : (a.thumbnail_url || a.url),
      );
      setComposerImagePreviews(prev => [...prev, ...previews]);
      return [...prevUrls, ...take.map(a => a.url)];
    });
  }, [composerImages.length]);

  const fetchScoops = useCallback(async (reset = false) => {
    const token = getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }
    if (!reset && feedPendingRef.current) return;
    const requestId = ++feedRequestRef.current;
    feedPendingRef.current = true;
    setFeedError('');
    if (reset) {
      offsetRef.current = 0;
      forYouCursorRef.current = null;
      forYouOverflowRef.current = false;
      overflowOffsetRef.current = 0;
      overflowDividerPendingRef.current = false;
      setOverflowStartId(null);
      setLoading(true);
      loadedReactionsRef.current.clear();
      setScoopReactions({});
    } else {
      setLoadingMore(true);
    }

    let continueFromDiscover = false;
    try {
      const overflow = activeTab === 'for-you' && forYouOverflowRef.current;
      const endpoint = overflow ? `${API_URL}/scoops/discover`
        : activeTab === 'for-you' ? `${API_URL}/scoops/for-you`
        : activeTab === 'following' ? `${API_URL}/scoops/stream`
        : activeTab === 'friends' ? `${API_URL}/scoops/friends`
        : activeTab === 'agents' ? `${API_URL}/agents-feed`
        : `${API_URL}/scoops/discover`;
      const offset = overflow ? overflowOffsetRef.current : offsetRef.current;
      // For You hides the sort control, so its Discover continuation is always newest-first.
      const params = new URLSearchParams({ limit: '20', offset: String(offset), sort: overflow ? 'new' : feedSort });
      if (designV2 && activeTab === 'discover') params.set('hubsOnly', '1');
      if (activeTab === 'for-you' && !overflow && forYouCursorRef.current) params.set('cursor', forYouCursorRef.current);
      let res = await fetch(`${endpoint}?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (requestId !== feedRequestRef.current) return;
      if (res.status === 409 && activeTab === 'for-you' && !overflow) {
        // A changed policy/expired session restarts once, under the same rules.
        reset = true;
        offsetRef.current = 0;
        forYouCursorRef.current = null;
        params.delete('cursor');
        params.set('offset', '0');
        res = await fetch(`${endpoint}?${params}`, { headers: { Authorization: `Bearer ${token}` } });
        if (requestId !== feedRequestRef.current) return;
      }
      if (res.status === 401) {
        navigate('/login');
        return;
      }
      if (!res.ok) {
        setFeedError(t('dashboardMain.feed.loadFailed', 'Could not load your feed. Please try again.'));
        return;
      }
      const data = await res.json();
      if (requestId !== feedRequestRef.current) return;
      const newScoops: ScoopPost[] = data.scoops || [];
      if (overflow) overflowOffsetRef.current += newScoops.length;
      else offsetRef.current += newScoops.length;
      if (activeTab === 'for-you' && !overflow) forYouCursorRef.current = data.nextCursor || null;

      if (reset) {
        seenScoopIdsRef.current = new Set(newScoops.map(s => s.id));
        dupePageCountRef.current = 0;
        setScoops(newScoops);
      } else {
        // Dedup against everything shown so far (ref = stable, no stale state).
        const uniqueNew = newScoops.filter(s => !seenScoopIdsRef.current.has(s.id));
        uniqueNew.forEach(s => seenScoopIdsRef.current.add(s.id));
        dupePageCountRef.current = uniqueNew.length === 0 ? dupePageCountRef.current + 1 : 0;
        if (uniqueNew.length > 0) {
          if (overflow && overflowDividerPendingRef.current) {
            overflowDividerPendingRef.current = false;
            // No divider when Discover supplies the very first posts: nothing sits above it.
            if (seenScoopIdsRef.current.size > uniqueNew.length) setOverflowStartId(uniqueNew[0].id);
          }
          setScoops(prev => [...prev, ...uniqueNew]);
        }
      }

      const serverHasMore = data.hasMore !== false;
      const poolExhausted = !serverHasMore || newScoops.length === 0 || dupePageCountRef.current >= 3;
      if (poolExhausted && activeTab === 'for-you' && !overflow) {
        forYouOverflowRef.current = true;
        overflowOffsetRef.current = 0;
        overflowDividerPendingRef.current = true;
        dupePageCountRef.current = 0;
        setHasMore(true);
        // A short last page would leave the sentinel in view with nothing to
        // scroll; fetch the first Discover page straight away.
        continueFromDiscover = newScoops.length < 20;
      } else {
        setHasMore(!poolExhausted);
      }

      // Resolve shortcodes for external URLs in loaded scoops
      const urlRegex = /https?:\/\/[^\s<>"')\]]+/g;
      const externalUrls = new Set<string>();
      for (const s of newScoops) {
        if (s.url && !s.url.includes('soltik.app')) externalUrls.add(s.url);
        const descUrls = (s.description || '').match(urlRegex) || [];
        for (const u of descUrls) { if (!u.includes('soltik.app')) externalUrls.add(u); }
      }
      if (externalUrls.size > 0) {
        fetch(`${API_URL}/links/resolve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ urls: [...externalUrls] }),
        })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d?.links) registerTrackedLinks(d.links); })
          .catch(() => {});
      }
    } catch {
      if (requestId === feedRequestRef.current) setFeedError(t('dashboardMain.feed.loadFailed', 'Could not load your feed. Please try again.'));
    } finally {
      if (requestId === feedRequestRef.current) {
        feedPendingRef.current = false;
        if (continueFromDiscover) {
          // Keep the skeleton/spinner up; the continuation clears it.
          void fetchScoopsRef.current(false);
        } else {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    }
  }, [activeTab, feedSort, navigate, designV2, t]);
  fetchScoopsRef.current = fetchScoops;

  const initialLoadDone = useRef(false);

  useEffect(() => {
    setPageContext(buildPageContext(pathname));
    return () => setPageContext(null);
  }, [pathname, setPageContext]);

  useEffect(() => {
    fetchScoops(true);
  }, [fetchScoops, feedRankingV2]);

  // Fetch suggested channels for new users
  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    fetch(`${API_URL}/scoops/suggested-channels`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        setSuggestedChannels(data.channels || []);
        setFollowingCount(data.followingCount ?? 0);
        // Pre-populate already-followed channels
        const alreadyFollowing = new Set<string>();
        (data.channels || []).forEach((ch: any) => {
          if (ch.is_following) alreadyFollowing.add(ch.id);
        });
        setFollowedChannelIds(alreadyFollowing);
      })
      .catch(() => {});
  }, []);

  const handleFollowChannel = async (channelId: string) => {
    const token = getAuthToken();
    if (!token) return;
    const res = await fetch(`${API_URL}/social/follow/${channelId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setFollowedChannelIds(prev => new Set(prev).add(channelId));
      setFollowingCount(prev => (prev ?? 0) + 1);
    }
  };

  const handleDismissWelcome = () => {
    setWelcomeDismissed(true);
    try { localStorage.setItem('soltik_welcome_dismissed', 'true'); } catch {}
  };

  // Activity streak ping
  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    fetch(`${API_URL}/activity/ping`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        setStreak(data.streak || 0);
        setFreezesAvailable(data.freezesAvailable ?? 0);
        setFreezeUsedToday(data.freezeUsedToday ?? false);
        if (data.newAchievements?.length > 0) {
          setNewAchievements(data.newAchievements);
          setShowAchievementToast(true);
          setTimeout(() => setShowAchievementToast(false), 6000);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch user's hubs for story post type
  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    fetch(`${API_URL}/shared-collections`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const allCollections = [...(data.collections || []), ...(data.shared || [])];
        const hubs = allCollections
          .filter((h: any) => h.hub_type)
          .map((h: any) => ({ id: h.id, name: h.title || h.name || 'Untitled Hub' }));
        setUserHubs(hubs);
        if (hubs.length > 0 && !storyHubId) setStoryHubId(hubs[0].id);
      })
      .catch(() => {});
  }, []);

  // Infinite scroll - load more when sentinel comes into view
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore && !feedError) {
          fetchScoops(false);
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, feedError, fetchScoops]);

  // Scroll-position fallback for infinite scroll. The IntersectionObserver
  // above is the primary trigger, but on some mobile browsers (notably iOS
  // Safari) it can fail to fire against the document scroller, so the feed
  // dead-ends at the bottom ("scroll to last post, nothing loads"). This
  // listener loads more whenever the viewport nears the bottom, regardless
  // of the sentinel. Guards mirror the observer so the two never double-fire.
  useEffect(() => {
    const onScroll = () => {
      if (!hasMore || loading || loadingMore || feedError) return;
      const doc = document.documentElement;
      const nearBottom = window.innerHeight + window.scrollY >= doc.scrollHeight - 900;
      if (nearBottom) fetchScoops(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [hasMore, loading, loadingMore, feedError, fetchScoops]);

  // Mark initial load done once we have data (no auto-switch needed; For You is default)
  useEffect(() => {
    if (!loading && scoops.length > 0) {
      initialLoadDone.current = true;
    }
  }, [loading, scoops.length]);

  // Fetch badges for visible scoop authors
  // Batch fetch badges for all authors in one request (was N+1)
  useEffect(() => {
    if (scoops.length === 0) return;
    const token = getAuthToken();
    if (!token) return;
    const authorIds = [...new Set(scoops.map(s => s.user_id).filter(Boolean))];
    const missing = authorIds.filter(id => !(id in userBadges));
    if (missing.length === 0) return;
    fetch(`${API_URL}/users/badges/batch?userIds=${missing.join(',')}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { badges: {} })
      .then(data => {
        const update: Record<string, any[]> = {};
        for (const id of missing) update[id] = data.badges?.[id] || [];
        setUserBadges(prev => ({ ...prev, ...update }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoops]);

  // Batch fetch reactions for all scoops in one request (was N+1)
  const fetchReactions = useCallback(async (scoopIds: string[]) => {
    const token = getAuthToken();
    if (!token || scoopIds.length === 0) return;
    try {
      const res = await fetch(`${API_URL}/scoops/reactions/batch?ids=${scoopIds.join(',')}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.reactions) setScoopReactions(prev => ({ ...prev, ...data.reactions }));
      }
    } catch {}
  }, []);

  // Load reactions for scoops
  useEffect(() => {
    const ids = scoops.filter(s => !loadedReactionsRef.current.has(s.id)).map(s => s.id);
    if (ids.length > 0) {
      ids.forEach(id => loadedReactionsRef.current.add(id));
      fetchReactions(ids);
    }
  }, [scoops, fetchReactions]);

  // Normalize URL — prepend https:// if missing
  const normalizeUrl = (url: string): string | null => {
    let u = url.trim();
    if (!u) return null;
    // Auto-prepend https:// if no protocol
    if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
    try { new URL(u); return u; } catch { return null; }
  };

  // Fetch link preview when URL changes
  const fetchLinkPreview = useCallback(async (rawUrl: string) => {
    setPreviewError(null);
    const token = getAuthToken();
    let normalized = rawUrl.trim();
    if (!normalized) { setLinkPreview(null); return; }
    if (!/^https?:\/\//i.test(normalized)) normalized = `https://${normalized}`;
    try { new URL(normalized); } catch { setLinkPreview(null); setPreviewError(t('dashboardMain.errors.invalidUrl', "Invalid URL")); return; }
    if (!token) { setLinkPreview(null); setPreviewError(t('dashboardMain.errors.notLoggedIn', "Not logged in")); return; }

    setLoadingPreview(true);
    setLinkPreview(null);
    try {
      const res = await fetch(`${API_URL}/url/metadata`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: normalized }),
      });
      const text = await res.text();
      let data: any;
      try { data = JSON.parse(text); } catch { setPreviewError(`Bad response: ${text.slice(0, 100)}`); setLoadingPreview(false); return; }

      if (res.ok && data.metadata) {
        const m = data.metadata;
        setLinkPreview({
          title: m.og_title || null,
          description: m.og_description || null,
          image: m.og_image || null,
          site_name: m.og_site_name || new URL(normalized).hostname,
          favicon: m.favicon || null,
        });
      } else {
        // API returned error or no metadata — show domain
        setLinkPreview({
          title: null,
          description: null,
          image: null,
          site_name: new URL(normalized).hostname,
          favicon: null,
        });
        if (data.error) setPreviewError(data.error);
      }
    } catch (err: any) {
      setPreviewError(err?.message || 'Network error');
      try {
        setLinkPreview({
          title: null,
          description: null,
          image: null,
          site_name: new URL(normalized).hostname,
          favicon: null,
        });
      } catch {
        setLinkPreview(null);
      }
    } finally {
      setLoadingPreview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (urlDebounceRef.current) clearTimeout(urlDebounceRef.current);
    if (!composerUrl.trim()) { setLinkPreview(null); return; }
    urlDebounceRef.current = setTimeout(() => fetchLinkPreview(composerUrl), 600);
    return () => { if (urlDebounceRef.current) clearTimeout(urlDebounceRef.current); };
  }, [composerUrl, fetchLinkPreview]);

  // Auto-detect URL pasted into textarea
  const handleComposerTextChange = (text: string) => {
    setComposerText(text);
    // Detect URL in text if no URL is set yet
    if (!composerUrl && !showUrlInput) {
      const urlMatch = text.match(/https?:\/\/[^\s]+/);
      if (urlMatch) {
        setComposerUrl(urlMatch[0]);
        setShowUrlInput(true);
        // Remove the URL from the text so it's not duplicated
        const cleaned = text.replace(urlMatch[0], '').trim();
        setComposerText(cleaned);
      }
    }
    // Detect @mention
    detectMention(text, composerRef.current, setMentionQuery, setMentionPos);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 4 - composerImages.length;
    const toAdd = files.slice(0, remaining);
    setComposerImages(prev => [...prev, ...toAdd]);
    toAdd.forEach(file => {
      // Videos use blob: URLs (createObjectURL) so the <video> element
      // can stream them. data: URLs work for images but the base64
      // string for a multi-MB video file is enormous and the video
      // element fails to decode it — preview shows 0:00 with no frame.
      if (file.type.startsWith('video/')) {
        const url = URL.createObjectURL(file);
        setComposerImagePreviews(prev => [...prev, url]);
      } else {
        const reader = new FileReader();
        reader.onload = () => setComposerImagePreviews(prev => [...prev, reader.result as string]);
        reader.readAsDataURL(file);
      }
    });
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const removeImage = (index?: number) => {
    if (index === undefined) {
      setComposerImages([]);
      setComposerImagePreviews([]);
      setPreUploadedUrls([]);
    } else {
      setComposerImages(prev => prev.filter((_, i) => i !== index));
      setComposerImagePreviews(prev => prev.filter((_, i) => i !== index));
    }
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  // Drag-drop accepts the same MIME list as the manual file picker.
  // Polls don't take attached media, so the drop zone disables itself
  // in that mode — drops on a poll-mode composer are ignored rather
  // than silently buffering files the user can't post.
  const { isDragging: isDraggingFiles, dropProps: composerDropProps } = useFileDrop({
    accept: 'image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm,video/x-m4v',
    maxFiles: Math.max(0, 4 - composerImages.length),
    enabled: postType !== 'poll' && composerImages.length < 4,
    onFiles: (files) => {
      const remaining = 4 - composerImages.length;
      const toAdd = files.slice(0, remaining);
      setComposerImages((prev) => [...prev, ...toAdd]);
      toAdd.forEach((file) => {
        if (file.type.startsWith('video/')) {
          // blob: URL — videos as base64 data URLs make the <video>
          // element show 0:00 with no frame.
          const url = URL.createObjectURL(file);
          setComposerImagePreviews((prev) => [...prev, url]);
        } else {
          const reader = new FileReader();
          reader.onload = () => setComposerImagePreviews((prev) => [...prev, reader.result as string]);
          reader.readAsDataURL(file);
        }
      });
    },
  });

  const addTag = (tag: string) => {
    const cleaned = tag.trim().toLowerCase().replace(/^#/, '').replace(/[^a-z0-9_-]/g, '');
    if (cleaned && !composerTags.includes(cleaned)) {
      setComposerTags(prev => [...prev, cleaned]);
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setComposerTags(prev => prev.filter(t => t !== tag));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      if (tagInput.trim()) addTag(tagInput);
    }
    if (e.key === 'Backspace' && !tagInput && composerTags.length > 0) {
      setComposerTags(prev => prev.slice(0, -1));
    }
  };

  const handlePost = async () => {
    const token = getAuthToken();
    if (!token || (!composerText.trim() && !composerUrl.trim() && composerImages.length === 0 && preUploadedUrls.length === 0 && postType !== 'poll' && postType !== 'event' && postType !== 'story' && postType !== 'status')) return;
    setPosting(true);

    // Extract inline #tags from text
    const tagMatches = composerText.match(/#[\w]+/g) || [];
    const parsedTags = tagMatches.map(t => t.slice(1).toLowerCase());
    const allTags = [...new Set([...parsedTags, ...composerTags])];
    // Strip #tags and any URLs from description (URL is sent separately)
    const cleanText = composerText
      .replace(/#[\w]+/g, '')
      .replace(/https?:\/\/[^\s]+/g, '')
      .trim();

    try {
      // Upload images if any
      let uploadedUrls: string[] = [];
      if (composerImages.length > 0) {
        setUploading(true);
        const formData = new FormData();
        composerImages.forEach(f => formData.append('files', f));
        const uploadRes = await fetch(`${API_URL}/scoops/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!uploadRes.ok) {
          const err = await uploadRes.json().catch(() => ({ error: t('dashboardMain.errors.uploadFailed', "Upload failed") }));
          setUploading(false);
          setPosting(false);
          setUploadError(err.code === 'nsfw_blocked' ? t('dashboardMain.errors.nsfwBlocked', "This image was rejected because it violates our content policy. Please upload appropriate content.") : (err.error || t('dashboardMain.errors.imageUploadFailed', "Image upload failed")));
          return;
        }
        const uploadData = await uploadRes.json();
        uploadedUrls = uploadData.urls || [];
        setUploading(false);
      }

      // Merge pre-uploaded media (voice notes, screen recordings) with freshly uploaded files
      const allMediaUrls = [...uploadedUrls, ...preUploadedUrls];

      // Detect video URL
      const normalizedUrl = normalizeUrl(composerUrl);
      const video = normalizedUrl ? detectVideoUrl(normalizedUrl) : null;

      // Determine content_type
      let content_type: string = postType === 'text' ? 'note' : postType;
      if (postType === 'story') {
        content_type = 'story';
      } else if (postType === 'status') {
        content_type = 'status';
      } else if (postType === 'text') {
        if (video) {
          content_type = 'video';
        } else if (normalizedUrl) {
          content_type = 'link';
        } else if (allMediaUrls.some(u => u.includes('.webm') || u.includes('.mp4'))) {
          content_type = 'video';
        } else if (allMediaUrls.length > 0) {
          content_type = 'image';
        }
      }

      const res = await fetch(`${API_URL}/scoops`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          description: cleanText || null,
          url: normalizedUrl || null,
          title: linkPreview?.title || null,
          image_url: allMediaUrls[0] || linkPreview?.image || null,
          media_urls: allMediaUrls.length > 0 ? allMediaUrls : undefined,
          content_type,
          metadata: video ? { videoId: video.id, videoPlatform: video.platform, embedUrl: video.embedUrl, thumbnailUrl: video.thumbnailUrl } : undefined,
          tags: allTags.length > 0 ? allTags : undefined,
          hub_id: postType === 'story' && storyHubId ? storyHubId : undefined,
          is_personal: false,
          is_gated: isGated || undefined,
          gate_tier_id: isGated && gateTierId ? gateTierId : undefined,
          mentions: composerMentions.length > 0 ? composerMentions : undefined,
          scheduled_at: scheduledAt || undefined,
          poll_options: postType === 'poll' ? pollOptions.filter(o => o.trim()) : undefined,
          poll_option_images: postType === 'poll' ? pollOptionImages : undefined,
          poll_expires_at: postType === 'poll' && pollExpiresAt ? pollExpiresAt : undefined,
          poll_multi_vote: postType === 'poll' ? pollMultiVote : undefined,
          event_starts_at: postType === 'event' ? eventStartsAt : undefined,
          event_ends_at: postType === 'event' && eventEndsAt ? eventEndsAt : undefined,
          event_location_url: postType === 'event' && eventLocationUrl ? eventLocationUrl : undefined,
        }),
      });
      if (res.ok) {
        const wasStory = postType === 'story';
        // Slice 4 — they've now posted, never show the first-post
        // prompt again on this device.
        markUserHasPosted();
        setComposerText('');
        setComposerUrl('');
        setComposerTags([]);
        setTagInput('');
        setShowUrlInput(false);
        setShowTagInput(false);
        setLinkPreview(null);
        setComposerMentions([]);
        setIsGated(false);
        setGateTierId('');
        setPostType('text');
        setPollOptions(['', '']);
        setPollOptionImages([null, null]);
        setPollExpiresAt('');
        setPollMultiVote(false);
        setEventStartsAt('');
        setEventEndsAt('');
        setEventLocationUrl('');
        setScheduledAt('');
        setShowSchedulePicker(false);
        removeImage();
        if (wasStory) {
          setStoryRefreshKey(k => k + 1);
        }
        fetchScoops(true);
      }
    } catch {
      // silently fail
    } finally {
      setPosting(false);
      setUploading(false);
    }
  };

  const likingPosts=useRef(new Set<string>());
  const handleLike = async (scoopId: string) => {
    const token = getAuthToken();
    if (!token) return;

    if(likingPosts.current.has(scoopId))return;
    likingPosts.current.add(scoopId);

    setScoops(prev => prev.map(s =>
      s.id === scoopId
        ? { ...s, viewer_liked: !s.viewer_liked, like_count: s.viewer_liked ? s.like_count - 1 : s.like_count + 1 }
        : s
    ));

    try {
      const result=await fetch(`${API_URL}/scoops/${scoopId}/like`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if(!result.ok)throw new Error('Like failed');
      const data=await result.json();
      setScoops(prev=>prev.map(s=>s.id===scoopId?{...s,viewer_liked:data.liked,like_count:data.like_count ?? s.like_count}:s));
      if(data.liked)trackEngage.like(scoopId,'feed');else trackEngage.unlike(scoopId,'feed');
    } catch {
      setScoops(prev => prev.map(s =>
        s.id === scoopId
          ? { ...s, viewer_liked: !s.viewer_liked, like_count: s.viewer_liked ? s.like_count - 1 : s.like_count + 1 }
          : s
      ));
    } finally { likingPosts.current.delete(scoopId); }
  };

  const handleScoopVote = async (scoopId: string) => {
    const token = getAuthToken();
    if (!token) return;

    setScoops(prev => prev.map(s =>
      s.id === scoopId
        ? { ...s, viewer_voted: !s.viewer_voted, scoop_count: s.viewer_voted ? (s.scoop_count || 0) - 1 : (s.scoop_count || 0) + 1 }
        : s
    ));

    try {
      await fetch(`${API_URL}/scoops/${scoopId}/vote`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      setScoops(prev => prev.map(s =>
        s.id === scoopId
          ? { ...s, viewer_voted: !s.viewer_voted, scoop_count: s.viewer_voted ? (s.scoop_count || 0) - 1 : (s.scoop_count || 0) + 1 }
          : s
      ));
    }
  };

  const handleTip = async (scoopId: string, amount: number, message?: string) => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/scoops/${scoopId}/tip`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, message }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast((err as any).error || t('dashboardMain.tip.tipFailed', "Tip failed"), 'error');
      }
    } catch {
      toast(t('dashboardMain.tip.failedToSendTip', "Failed to send tip"), 'error');
    }
  };

  const handleReaction = async (scoopId: string, emoji: string) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_URL}/scoops/${scoopId}/react`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji }),
      });
      if (res.ok) {
        const data = await res.json();
        setScoopReactions(prev => ({ ...prev, [scoopId]: data.reactions }));
        trackEngage.reaction(scoopId, emoji, 'feed');
      }
    } catch {}
  };

  const handleDelete = async (scoopId: string) => {
    const token = getAuthToken();
    if (!token) return;
    setScoops(prev => prev.filter(s => s.id !== scoopId));
    try {
      const res = await fetch(`${API_URL}/scoops/${scoopId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        // Roll back — refetch the feed
        fetchScoops(true);
      }
    } catch {
      fetchScoops(true);
    }
  };

  const handleReshare = async (scoopId: string, comment?: string) => {
    const token = getAuthToken();
    if (!token) return;


    setScoops(prev => prev.map(s =>
      s.id === scoopId ? { ...s, reshare_count: s.reshare_count + 1 } : s
    ));

    try {
      const result=await fetch(`${API_URL}/scoops/${scoopId}/reshare`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(comment?.trim() ? { comment: comment.trim() } : {}),
      });
      if(!result.ok)throw new Error('Share failed');
      trackEngage.share(scoopId,'feed');
      await flushEvents();
      fetchScoops(true);
    } catch {
      setScoops(prev => prev.map(s =>
        s.id === scoopId ? { ...s, reshare_count: s.reshare_count - 1 } : s
      ));
    }
  };

  const handleReply = async (scoopId: string, content: string, mentions?: MentionData[]) => {
    const token = getAuthToken();
    if (!token || !content.trim()) return;


    try {
      const res = await fetch(`${API_URL}/scoops/${scoopId}/replies`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: content.trim(), mentions: mentions && mentions.length > 0 ? mentions : undefined }),
      });
      if (res.ok) {
        trackEngage.comment(scoopId,'feed');
        setScoops(prev => prev.map(s =>
          s.id === scoopId ? { ...s, reply_count: s.reply_count + 1 } : s
        ));
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleEdit = async (scoopId: string, updates: { description?: string; tags?: string[]; url?: string | null; title?: string | null; media_urls?: any[] | null; image_url?: string | null; content_type?: string; metadata?: any }) => {
    const token = getAuthToken();
    if (!token) return false;
    try {
      const res = await fetch(`${API_URL}/scoops/${scoopId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        const data = await res.json();
        setScoops(prev => prev.map(s => {
          if (s.id !== scoopId) return s;
          const patched = { ...s, edited_at: data.scoop?.edited_at || new Date().toISOString() };
          if (updates.description !== undefined) patched.description = updates.description || null;
          if (updates.tags !== undefined) patched.tags = updates.tags || [];
          if (updates.url !== undefined) { patched.url = updates.url; if (updates.url === null) { patched.title = null; patched.image_url = null; } }
          if (updates.title !== undefined) patched.title = updates.title;
          if (updates.media_urls !== undefined) patched.media_urls = updates.media_urls || [];
          if (updates.image_url !== undefined) patched.image_url = updates.image_url;
          if (updates.content_type !== undefined) patched.content_type = updates.content_type;
          if (updates.metadata !== undefined) patched.metadata = updates.metadata;
          return patched;
        }));
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleReport = async (scoopId: string, category: string, description?: string): Promise<boolean> => {
    const token = getAuthToken();
    if (!token) return false;
    try {
      const res = await fetch(`${API_URL}/content/report`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content_type: 'scoop',
          content_id: scoopId,
          category,
          details: description,
        }),
      });
      return res.ok;
    } catch { return false; }
  };

  const formatTime = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('time.justNow', "just now");
    if (mins < 60) return t('time.minutesShort', { defaultValue: "{count}m", count: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t('time.hoursShort', { defaultValue: "{count}h", count: hrs });
    const days = Math.floor(hrs / 24);
    if (days < 30) return t('time.daysShort', { defaultValue: "{count}d", count: days });
    return formatDate(date);
  };

  const getAuthorDisplayName = (scoop: ScoopPost) => {
    if (scoop.author_name) return scoop.author_name;
    if (scoop.author_display_name) return scoop.author_display_name;
    if (scoop.author_username) return `@${scoop.author_username}`;
    return t('dashboardMain.card.authorFallback', 'User');
  };

  return (
    <div
      className="mx-auto px-4 py-6"
      style={{ maxWidth: '640px' }}
    >
      {/* Streak Badge — hidden under designV2 (the mock moves stats to the right rail
          and keeps the feed column chrome-free; we still let users see streaks via Profile). */}
      {!designV2 && streak > 0 && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl bg-linear-to-r/srgb from-orange-500/10 to-amber-500/10 border border-orange-500/20">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-linear-to-br/srgb from-orange-500 to-amber-500">
            <Flame size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-agent-gradient">{streak}</span>
              <span className="text-sm text-orange-300 font-medium">{t('streak.dayStreak', 'day streak')}</span>
            </div>
            <p className="text-xs text-[#a0a0b0]">
              {freezeUsedToday
                ? t('streak.freezeUsed', 'Streak freeze saved your streak!')
                : t('streak.keepGoing', 'Keep visiting daily to grow your streak!')}
            </p>
          </div>
          <div className="flex flex-col items-center gap-1">
            {streak >= 7 && (
              <div className="text-2xl">{streak >= 100 ? '\uD83D\uDD25' : streak >= 30 ? '\u2B50' : '\uD83D\uDCAA'}</div>
            )}
            {freezesAvailable > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/20">
                {freezesAvailable} {freezesAvailable === 1 ? 'freeze' : 'freezes'}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Achievement Celebration Modal */}
      {showAchievementToast && newAchievements.length > 0 && (() => {
        const achLabels: Record<string, string> = {
          streak_3: '3-Day Streak', streak_7: '7-Day Streak', streak_30: '30-Day Streak', streak_100: '100-Day Streak',
          first_post: 'First Post', first_site: 'Launched a Site', first_shop: 'Opened a Shop', first_sale: 'First Sale',
          friends_5: '5 Friends', friends_25: '25 Friends', followers_100: '100 Followers', followers_1000: '1K Followers',
          founding_member: 'Founding Member',
          on_fire: 'On Fire!', posts_10: '10 Posts', posts_50: '50 Posts', posts_100: '100 Posts',
          likes_100: '100 Likes Received', likes_1000: '1K Likes Received',
          streak_freeze_used: 'Streak Saved!',
        };
        const achEmoji: Record<string, string> = {
          streak_3: '\uD83D\uDD25', streak_7: '\uD83D\uDD25', streak_30: '\u2B50', streak_100: '\uD83D\uDC8E',
          first_post: '\uD83D\uDCDD', first_site: '\uD83C\uDF10', first_shop: '\uD83D\uDED2', first_sale: '\uD83D\uDCB0',
          friends_5: '\uD83E\uDD1D', friends_25: '\uD83E\uDD1D', followers_100: '\uD83D\uDE80', followers_1000: '\uD83C\uDF1F',
          founding_member: '\uD83D\uDC51',
          on_fire: '\uD83D\uDD25', posts_10: '\u270D\uFE0F', posts_50: '\uD83D\uDCDA', posts_100: '\uD83C\uDFC6',
          likes_100: '\u2764\uFE0F', likes_1000: '\uD83D\uDE80',
          streak_freeze_used: '\u2744\uFE0F',
        };
        const primary = newAchievements[0];
        const label = achLabels[primary] || primary;
        const emoji = achEmoji[primary] || '\uD83C\uDFC6';
        const userId = localStorage.getItem('userId') || '';
        const cardUrl = `${window.location.origin}/achievement/${userId}/${primary}`;
        const shareText = `${emoji} I just unlocked "${label}" on Soltik! Join me:`;

        const shareOn = (platform: string) => {
          const urls: Record<string, string> = {
            x: `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(cardUrl)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(cardUrl)}&quote=${encodeURIComponent(shareText)}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(cardUrl)}`,
            whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + cardUrl)}`,
            telegram: `https://t.me/share/url?url=${encodeURIComponent(cardUrl)}&text=${encodeURIComponent(shareText)}`,
            reddit: `https://reddit.com/submit?url=${encodeURIComponent(cardUrl)}&title=${encodeURIComponent(shareText)}`,
          };
          if (urls[platform]) window.open(urls[platform], '_blank', 'width=600,height=500');
        };

        return (
          <div className="fixed inset-0 z-100 flex items-center justify-center bg-black/60 backdrop-blur-xs" onClick={() => setShowAchievementToast(false)}>
            <div className="p-6 rounded-2xl bg-[#16161e] border border-white/10 shadow-2xl max-w-sm w-[90%] text-center" onClick={e => e.stopPropagation()}>
              <div className="text-5xl mb-3">{emoji}</div>
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-linear-to-br/srgb from-iris-500 to-iris-500 flex items-center justify-center">
                <Trophy size={32} className="text-yellow-300" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Achievement Unlocked!</h3>
              <p className="text-iris-300 font-semibold text-base mb-1">{label}</p>
              {newAchievements.length > 1 && (
                <p className="text-xs text-[#a0a0b0] mb-3">+{newAchievements.length - 1} more: {newAchievements.slice(1).map(a => achLabels[a] || a).join(', ')}</p>
              )}
              <p className="text-sm text-[#a0a0b0] mb-5">Share your achievement with the world!</p>
              <div className="grid grid-cols-6 gap-2 mb-4">
                {[
                  { key: 'x', label: 'X', color: '#000' },
                  { key: 'facebook', label: 'FB', color: '#1877F2' },
                  { key: 'linkedin', label: 'In', color: '#0A66C2' },
                  { key: 'whatsapp', label: 'WA', color: '#25D366' },
                  { key: 'telegram', label: 'TG', color: '#26A5E4' },
                  { key: 'reddit', label: 'Re', color: '#FF4500' },
                ].map(p => (
                  <button key={p.key} onClick={() => shareOn(p.key)} className="p-2 rounded-lg text-white text-xs font-bold hover:opacity-80 transition-opacity" style={{ background: p.color }}>
                    {p.label}
                  </button>
                ))}
              </div>
              <button onClick={() => setShowAchievementToast(false)} className="w-full py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-[#a0a0b0] hover:text-white hover:bg-white/10 transition-all">
                Close
              </button>
            </div>
          </div>
        );
      })()}

      {/* Daily Prompt — hidden under designV2 (mock keeps the feed column free of banners;
          the prompt is still accessible from Studio if a user wants writing nudges). */}
      {!designV2 && (
        <DailyPromptCard onUsePrompt={(text) => {
          setComposerText(text + '\n\n');
          composerRef.current?.focus();
        }} />
      )}

      {/* Welcome Card for new users (0 follows, not dismissed) */}
      {followingCount !== null && followingCount === 0 && !welcomeDismissed && suggestedChannels.length > 0 && (
        <div className="mb-6 rounded-2xl bg-linear-to-br/srgb from-iris-600/10 via-violet-600/10 to-fuchsia-600/10 border border-iris-500/20 overflow-hidden">
          <div className="p-5 pb-3">
            <div className="flex items-start justify-between mb-1 flex-wrap gap-y-2">
              <div>
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  {t('dashboardMain.welcome.title', 'Welcome to Soltik!')}
                  <span className="text-lg" role="img" aria-label="wave">&#128075;</span>
                </h3>
                <p className="text-sm text-[#a0a0b0] mt-1">
                  {t('dashboardMain.welcome.subtitle', 'Follow channels to build your personalized feed:')}
                </p>
              </div>
              <button
                onClick={handleDismissWelcome}
                className="p-1.5 rounded-lg text-[#9a9aac] hover:text-white hover:bg-white/10 transition-colors shrink-0"
                title={t('common.dismiss', 'Dismiss')}
              >
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="relative">
            <div className="px-5 pb-5 overflow-x-auto">
              <div className="flex gap-3" style={{ minWidth: 'min-content' }}>
                {suggestedChannels.slice(0, 8).map(channel => (
                <Link
                  key={channel.id}
                  to={`/u/${channel.username}`}
                  className="shrink-0 w-[160px] p-3.5 rounded-xl bg-[#16161e]/80 border border-white/6 hover:border-iris-500/40 hover:bg-[#16161e] transition-all block cursor-pointer"
                  aria-label={`Open ${channel.display_name || channel.username}`}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-linear-to-br/srgb from-iris-500 to-violet-600 flex items-center justify-center text-[#0C0C12] text-base font-semibold overflow-hidden mb-2.5">
                      {channel.avatar_url ? (
                        <img src={channel.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        channel.display_name?.charAt(0)?.toUpperCase() || channel.username?.charAt(0)?.toUpperCase() || '?'
                      )}
                    </div>
                    <h4 className="text-xs font-semibold text-white truncate w-full">{channel.display_name || channel.username}</h4>
                    <p className="text-[10px] text-[#9a9aac] truncate w-full capitalize">{channel.category}</p>
                    <p className="text-[10px] text-[#828294] mt-1">{channel.post_count} {t('common.posts', 'posts')}</p>
                    <button
                      type="button"
                      onClick={(e) => {
                        // Stop the Link from firing a profile navigation when
                        // the user only meant to Follow/Unfollow.
                        e.preventDefault();
                        e.stopPropagation();
                        if (!followedChannelIds.has(channel.id)) handleFollowChannel(channel.id);
                      }}
                      disabled={followedChannelIds.has(channel.id)}
                      className={`mt-2.5 w-full px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all flex items-center justify-center gap-1 ${
                        followedChannelIds.has(channel.id)
                          ? 'bg-white/6 text-[#9a9aac]'
                          : 'bg-iris-600 hover:bg-iris-500 text-[#0C0C12] cursor-pointer'
                      }`}
                    >
                      {followedChannelIds.has(channel.id) ? (
                        <><CheckCircle2 size={11} /> {t('common.following', 'Following')}</>
                      ) : (
                        <><Users size={11} /> {t('common.follow', 'Follow')}</>
                      )}
                    </button>
                  </div>
                </Link>
              ))}
              </div>
            </div>
            {/* Right-edge fade hint that more content scrolls horizontally — pure CSS, ignores pointer events. */}
            <div aria-hidden className="absolute top-0 right-0 bottom-5 w-12 pointer-events-none bg-linear-to-l/srgb from-[#0a0a0f] to-transparent" />
          </div>
          <div className="px-5 pb-4">
            <p className="text-xs text-[#9a9aac]">
              {t('dashboardMain.welcome.exploreHint', 'Or explore the discover feed below to find more content.')}
            </p>
          </div>
        </div>
      )}

      {/* Suggested Channels Row (for users following < 3 accounts, after welcome is dismissed) */}
      {followingCount !== null && followingCount < 3 && followingCount > 0 && suggestedChannels.length > 0 && (activeTab === 'discover' || activeTab === 'for-you') && (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-y-2">
            <h3 className="text-sm font-semibold text-white">{t('dashboardMain.suggestedChannels.title', 'Suggested Channels')}</h3>
          </div>
          <div className="overflow-x-auto -mx-1 px-1">
            <div className="flex gap-2.5" style={{ minWidth: 'min-content' }}>
              {suggestedChannels.filter(ch => !followedChannelIds.has(ch.id)).slice(0, 6).map(channel => (
                <Link
                  key={channel.id}
                  to={`/u/${channel.username}`}
                  className="shrink-0 w-[150px] p-3 rounded-xl bg-surface-elevated border border-white/6 hover:border-iris-500/40 hover:bg-[#16161e] transition-all block cursor-pointer"
                  aria-label={`Open ${channel.display_name || channel.username}`}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-10 h-10 rounded-full bg-linear-to-br/srgb from-iris-500 to-violet-600 flex items-center justify-center text-[#0C0C12] text-sm font-semibold overflow-hidden mb-2">
                      {channel.avatar_url ? (
                        <img src={channel.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        channel.display_name?.charAt(0)?.toUpperCase() || '?'
                      )}
                    </div>
                    <h4 className="text-[11px] font-semibold text-white truncate w-full">{channel.display_name || channel.username}</h4>
                    <p className="text-[10px] text-[#828294] capitalize">{channel.category}</p>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleFollowChannel(channel.id); }}
                      className="mt-2 w-full px-2.5 py-1.5 rounded-lg text-[10px] font-medium bg-iris-600 hover:bg-iris-500 text-[#0C0C12] transition-all flex items-center justify-center gap-1 cursor-pointer"
                    >
                      <Users size={10} /> {t('common.follow', 'Follow')}
                    </button>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Phase 23a — Admin-targeted prompt/poll (Daily Prompt) */}
      <TargetedPromptCard />

      {/* Tabs + Sort — sticky so they stay reachable while scrolling the feed.
          Agentic theme: glow-bordered pill rail, gradient highlight on the
          active tab. */}
      <div className="mb-6 space-y-2 sticky top-14 lg:top-0 z-20 bg-[#0a0a0f]/90 backdrop-blur-xl -mx-4 px-4 pt-2 pb-2">
        <div
          role="tablist"
          aria-label={t('dashboardMain.tabsLabel', 'Feed tabs')}
          className="flex gap-1 p-1 rounded-xl bg-white/3 border border-white/8 shadow-[0_0_30px_rgba(99,102,241,0.05)] overflow-x-auto"
        >
          {/* V2 collapses the tab strip to match the mock's For You / Following / Hubs
              (Hubs reuses the existing 'discover' endpoint — the feed it returns is
              the cross-hub mix). 'friends' / 'agents' surfaces stay reachable through
              direct routes; folding them into the main 3-tab rail keeps the chrome
              clean per docs/design/mocks/dashboard-feed.html. */}
          {((designV2
            ? (['for-you', 'following', 'discover'] as FeedTab[])
            : (['for-you', 'following', 'friends', 'agents', 'discover'] as FeedTab[])
          )).map(tab => (
            <button
              key={tab}
              role="tab"
              aria-selected={activeTab === tab}
              tabIndex={activeTab === tab ? 0 : -1}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 min-w-fit py-2.5 px-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap focus-visible:ring-2 focus-visible:ring-iris-400/60 focus-visible:outline-hidden ${
                activeTab === tab
                  ? 'bg-linear-to-r/srgb from-iris-500/20 to-iris-500/20 text-white shadow-[inset_0_0_0_1px_rgba(165,180,252,0.2)]'
                  : 'text-[#8e8ea0] hover:text-white hover:bg-white/4'
              }`}
            >
              {tab === 'for-you' ? t('dashboardMain.tabs.forYou', 'For You')
                : tab === 'following' ? t('dashboardMain.tabs.following', "Following")
                : tab === 'friends' ? t('dashboardMain.tabs.friends', 'Friends')
                : tab === 'agents' ? t('dashboardMain.tabs.agents', 'Agents')
                : designV2 ? t('dashboardMain.tabs.hubs', 'Hubs')
                : t('dashboardMain.tabs.discover', "Discover")}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 px-1">
          {activeTab !== 'for-you' && (['new', 'hot', 'trending'] as FeedSortMode[]).map(mode => (
            <button
              key={mode}
              onClick={() => setFeedSort(mode)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-iris-400/60 focus-visible:outline-hidden ${
                feedSort === mode
                  ? 'bg-iris-500/15 text-iris-200 shadow-[inset_0_0_0_1px_rgba(165,180,252,0.25)]'
                  : 'text-[#8e8ea0] hover:text-white hover:bg-white/4'
              }`}
            >
              {mode === 'new' ? t('dashboardMain.sort.new', "New") : mode === 'hot' ? t('dashboardMain.sort.hot', "Hot") : t('dashboardMain.sort.trending', "Trending")}
            </button>
          ))}
        </div>
      </div>

      {activeTab==='for-you' && <div className="flex gap-4 text-xs text-iris-300 mb-4">
        <Link to="/dashboard/settings/personalization">{t('dashboardMain.feed.preferencesLink', "Feed preferences")}</Link>
        <Link to="/dashboard/saved">{t('dashboardMain.feed.savedLink', "Saved posts")}</Link>
      </div>}
      {/* Story ring — aggregated from all user's hubs */}
      <FeedStoryRing refreshKey={storyRefreshKey} />

      {/* First-post nudge — only renders for users with zero scoops */}
      {activeTab === 'for-you' && (
        <FirstPostPrompt
          onUseTemplate={(text) => {
            setComposerText(text);
            composerRef.current?.focus();
            // Move caret to end so they can keep typing.
            requestAnimationFrame(() => {
              const el = composerRef.current;
              if (el) el.setSelectionRange(text.length, text.length);
            });
          }}
        />
      )}

      {/* Social Proof Nudge */}
      <SocialProofNudge />

      {/* Post Suggestion for creators */}
      <PostSuggestionBanner onCreatePost={(text) => {
        setComposerText(text + '\n\n');
        composerRef.current?.focus();
      }} />

      {/* Removed: the v2 StoriesRow used to render here, but it
          duplicated FeedStoryRing above — both surfaced a "+" /
          "Your Story" button on the same feed and only differed in
          which composer they opened (hub story vs personal story).
          The duplicated UI confused users into thinking there were
          two different actions. Keep FeedStoryRing only. */}

      {/* Composer — drop target for drag-and-drop files. The dashed
          ring + tinted background snap on while a file is being
          dragged over the composer area. */}
      <div
        {...composerDropProps}
        className={`mb-6 rounded-2xl bg-surface-elevated border overflow-hidden transition-all focus-within:border-iris-400/40 focus-within:shadow-[0_0_0_1px_rgba(129,140,248,0.2),0_8px_32px_rgba(99,102,241,0.08)] ${
          isDraggingFiles
            ? 'border-iris-500/60 ring-2 ring-iris-500/30 bg-iris-500/4'
            : 'border-white/8'
        }`}>
        <div className="p-4 pb-2 relative">
          <textarea
            ref={composerRef}
            value={composerText}
            onChange={(e) => handleComposerTextChange(e.target.value)}
            onKeyDown={(e) => {
              if (mentionQuery !== null) return; // let MentionDropdown handle keys
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handlePost();
              }
            }}
            placeholder={t('composer.placeholder', "What's on your mind? Share a link, insight, or hot take... Type @ to mention someone")}
            className="w-full bg-transparent text-white placeholder:text-[#4e4e60] resize-none text-sm leading-relaxed focus:outline-hidden min-h-[60px]"
            rows={2}
          />
          {mentionQuery !== null && (
            <MentionDropdown
              query={mentionQuery}
              position={mentionPos}
              onSelect={(user) => handleMentionSelect(user, composerText, setComposerText, composerRef.current, composerMentions, setComposerMentions, setMentionQuery)}
              onClose={() => setMentionQuery(null)}
            />
          )}

          {/* AI Post Writer */}
          {!composerText.trim() && !linkPreview && !aiWriting && (
            <div className="mt-2">
              {!showAIWriter ? (
                <button
                  onClick={() => setShowAIWriter(true)}
                  className="inline-flex items-center gap-1.5 py-1.5 min-h-[24px] text-xs text-violet-400 hover:text-violet-300 transition-colors"
                >
                  <Sparkles size={12} />
                  <span>{t('chromeAI.writeWithAI', "Write with AI")}</span>
                </button>
              ) : (
                <div className="rounded-xl bg-violet-500/6 border border-violet-500/15 p-3 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-violet-300 flex items-center gap-1.5">
                      <Sparkles size={12} />
                      {t('chromeAI.writeWithAI', "Write with AI")}
                    </span>
                    <button onClick={() => setShowAIWriter(false)} className="p-0.5 rounded text-violet-400/50 hover:text-violet-300 transition-colors">
                      <X size={12} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { label: t('chromeAI.promptAnnounce', "Announcement"), prompt: 'Write an announcement post' },
                      { label: t('chromeAI.promptTip', "Share a tip"), prompt: 'Share a helpful tip or insight' },
                      { label: t('chromeAI.promptQuestion', "Ask a question"), prompt: 'Ask an engaging question to start a conversation' },
                    ].map(({ label, prompt }) => (
                      <button
                        key={label}
                        onClick={() => handleAIWrite(prompt)}
                        className="px-2.5 py-1 rounded-lg bg-violet-500/10 border border-violet-500/20 text-[11px] text-violet-300 hover:bg-violet-500/20 hover:border-violet-500/30 transition-all"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      value={aiWritePrompt}
                      onChange={(e) => setAiWritePrompt(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && aiWritePrompt.trim()) {
                          e.preventDefault();
                          handleAIWrite(aiWritePrompt);
                        }
                      }}
                      placeholder={t('chromeAI.writePromptPlaceholder', "Describe what you want to post about...")}
                      className="flex-1 bg-white/4 rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-[#4e4e60] focus:outline-hidden border border-white/6 focus:border-violet-500/40 transition-colors"
                    />
                    <button
                      onClick={() => handleAIWrite(aiWritePrompt)}
                      disabled={!aiWritePrompt.trim()}
                      className="p-1.5 rounded-lg bg-violet-500/20 text-violet-300 hover:bg-violet-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <Sparkles size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* AI Writing indicator */}
          {aiWriting && (
            <div className="mt-2 flex items-center gap-2 text-xs text-violet-400">
              <Loader2 size={12} className="animate-spin" />
              <span>{t('chromeAI.writingPost', "Writing your post...")}</span>
            </div>
          )}

          {/* Link input + preview */}
          {showUrlInput && (
            <div className="mt-2 space-y-2">
              {/* URL text input — hide once preview is loaded */}
              {!linkPreview && !loadingPreview && (
                <div className="flex items-center gap-2">
                  <Link2 size={14} className="text-iris-400 shrink-0" />
                  <input
                    type="url"
                    value={composerUrl}
                    onChange={(e) => setComposerUrl(e.target.value)}
                    placeholder="https://..."
                    autoFocus
                    className="flex-1 bg-white/4 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#4e4e60] focus:outline-hidden border border-white/6 focus:border-iris-500/40 transition-colors"
                  />
                  <button
                    onClick={() => { setShowUrlInput(false); setComposerUrl(''); setLinkPreview(null); setPreviewError(null); }}
                    className="p-1.5 rounded-md text-[#9a9aac] hover:text-white hover:bg-white/5 transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Loading state */}
              {loadingPreview && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-white/2 border border-white/4">
                  <Loader2 size={14} className="animate-spin text-iris-400" />
                  <span className="text-xs text-[#9a9aac]">{t('composer.fetchingPreview', "Fetching preview...")}</span>
                </div>
              )}

              {/* Error */}
              {previewError && !loadingPreview && !linkPreview && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-red-500/6 border border-red-500/10">
                  <span className="text-[12px] text-red-400">{previewError}</span>
                </div>
              )}

              {/* Preview card — replaces URL input once loaded (skip for video URLs) */}
              {linkPreview && !loadingPreview && !detectVideoUrl(composerUrl.startsWith('http') ? composerUrl : `https://${composerUrl}`) && (
                <div className="relative rounded-xl border border-white/6 bg-white/2 overflow-hidden group">
                  <button
                    onClick={() => { setShowUrlInput(false); setComposerUrl(''); setLinkPreview(null); setPreviewError(null); }}
                    className="absolute top-2 right-2 z-10 p-1 rounded-full bg-black/60 text-white/70 hover:text-white hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <X size={12} />
                  </button>
                  {linkPreview.image && (
                    <div className="h-32 overflow-hidden bg-white/2">
                      <img src={linkPreview.image} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-3">
                    {linkPreview.title && (
                      <h4 className="text-sm font-medium text-white mb-0.5 line-clamp-1">{linkPreview.title}</h4>
                    )}
                    {linkPreview.description && (
                      <p className="text-xs text-[#8e8ea0] line-clamp-2 mb-1.5">{linkPreview.description}</p>
                    )}
                    <div className="flex items-center gap-1.5 text-[12px] text-[#9a9aac]">
                      {linkPreview.favicon && (
                        <img src={linkPreview.favicon} alt="" className="w-3 h-3 rounded-xs" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      )}
                      <span>{linkPreview.site_name || (() => { const n = normalizeUrl(composerUrl); if (n) try { return new URL(n).hostname; } catch { return ''; } return ''; })()}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Video preview */}
          {(() => {
            const vid = composerUrl ? detectVideoUrl(composerUrl.startsWith('http') ? composerUrl : `https://${composerUrl}`) : null;
            if (!vid) return null;
            return (
              <div className="mt-2 relative rounded-xl border border-white/6 overflow-hidden bg-black">
                {vid.thumbnailUrl ? (
                  <div className="relative">
                    <img src={vid.thumbnailUrl} alt="Video" className="w-full h-40 object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="w-12 h-12 rounded-full bg-black/60 flex items-center justify-center">
                        <Play size={20} className="text-white ms-0.5" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-32 flex items-center justify-center bg-white/2">
                    <Play size={24} className="text-[#9a9aac]" />
                  </div>
                )}
                <div className="px-3 py-2 flex items-center gap-2">
                  <span className="text-[12px] uppercase font-medium tracking-wider text-iris-400">{vid.platform}</span>
                  <span className="text-xs text-[#9a9aac] truncate">{composerUrl}</span>
                </div>
              </div>
            );
          })()}

          {/* Image preview grid */}
          {composerImagePreviews.length > 0 && (
            <div className="mt-2">
              <div className={`grid gap-1 rounded-xl overflow-hidden ${
                composerImagePreviews.length === 1 ? 'grid-cols-1' :
                composerImagePreviews.length === 2 ? 'grid-cols-2' :
                composerImagePreviews.length === 3 ? 'grid-cols-2 grid-rows-2' :
                'grid-cols-2 grid-rows-2'
              }`} style={{ maxHeight: 240 }}>
                {composerImagePreviews.map((preview, i) => (
                  <div
                    key={i}
                    className={`relative group/img ${
                      composerImagePreviews.length === 3 && i === 0 ? 'row-span-2' : ''
                    }`}
                  >
                    {preview === '__audio__' ? (
                      <div className={`w-full flex flex-col items-center justify-center bg-iris-500/10 border border-iris-500/20 ${
                        composerImagePreviews.length === 1 ? 'h-20 rounded-xl' : 'h-[120px]'
                      }`}>
                        <Mic size={24} className="text-iris-400 mb-1" />
                        <span className="text-[11px] text-iris-300 font-medium">Voice Note</span>
                      </div>
                    ) : (composerImages[i]?.type?.startsWith('video/') || preview.startsWith('data:video/') || /\.(webm|mp4|mov|ogg)(\?|$)/i.test(preview)) ? (
                      <video
                        src={preview}
                        controls
                        className={`w-full border border-white/6 ${
                          composerImagePreviews.length === 1 ? 'max-h-60 rounded-xl' :
                          composerImagePreviews.length === 3 && i === 0 ? 'h-full rounded-l-xl' :
                          'h-full'
                        } ${composerImagePreviews.length >= 2 ? 'h-[120px]' : ''} ${
                          composerImagePreviews.length === 3 && i === 0 ? 'h-full!' : ''
                        }`}
                      />
                    ) : (
                      <img
                        src={preview}
                        alt={`Upload ${i + 1}`}
                        className={`w-full object-cover border border-white/6 ${
                          composerImagePreviews.length === 1 ? 'max-h-60 rounded-xl' :
                          composerImagePreviews.length === 3 && i === 0 ? 'h-full rounded-l-xl' :
                          'h-full'
                        } ${composerImagePreviews.length >= 2 ? 'h-[120px]' : ''} ${
                          composerImagePreviews.length === 3 && i === 0 ? 'h-full!' : ''
                        }`}
                      />
                    )}
                    <div className="absolute top-1.5 right-1.5 flex items-center gap-1 opacity-0 group-hover/img:opacity-100 transition-opacity">
                      {preview !== '__audio__' && composerImages[i] && (
                        <button
                          onClick={() => { setImageToEdit({ file: composerImages[i], index: i }); setShowImageEditor(true); }}
                          className="p-1 rounded-full bg-black/70 text-white hover:bg-black/90 transition-colors"
                          title={t('composer.editImage', "Edit image")}
                        >
                          <Pencil size={12} />
                        </button>
                      )}
                      <button
                        onClick={() => removeImage(i)}
                        className="p-1 rounded-full bg-black/70 text-white hover:bg-black/90 transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-1 text-[12px] text-[#9a9aac]">
                {composerImagePreviews.some(p => p === '__audio__')
                  ? `${composerImagePreviews.length}/4 media`
                  : t('composer.imagesCount', { defaultValue: "{count}/4 images", count: composerImagePreviews.length })}
              </div>
            </div>
          )}

          {/* Post type picker */}
          <div className="flex items-center gap-1 mt-2">
            {([
              { key: 'text' as const, label: t('dashboardMain.composer.postTypeText', "Text"), icon: null, color: '' },
              { key: 'status' as const, label: t('dashboardMain.composer.postTypeStatus', "Status"), icon: SmilePlus, color: 'cyan' },
              { key: 'poll' as const, label: t('dashboardMain.composer.postTypePoll', "Poll"), icon: BarChart2, color: 'purple' },
              { key: 'event' as const, label: t('dashboardMain.composer.postTypeEvent', "Event"), icon: Calendar, color: 'orange' },
              ...(userHubs.length > 0 ? [{ key: 'story' as const, label: t('stories.createStory', "Create Story"), icon: Clock, color: 'pink' }] : []),
            ]).map(({ key, label, icon: Icon, color }) => (
              <button
                key={key}
                onClick={() => setPostType(key)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                  postType === key
                    ? color === 'purple' ? 'bg-iris-500/15 text-iris-400 border border-iris-500/30'
                    : color === 'orange' ? 'bg-orange-500/15 text-orange-400 border border-orange-500/30'
                    : color === 'pink' ? 'bg-pink-500/15 text-pink-400 border border-pink-500/30'
                    : color === 'cyan' ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30'
                    : 'bg-white/10 text-white border border-white/10'
                    : 'text-[#9a9aac] hover:text-white hover:bg-white/5 border border-transparent'
                }`}
              >
                {Icon && <Icon size={12} />}
                {label}
              </button>
            ))}
          </div>

          {/* Poll options */}
          {postType === 'poll' && (
            <div className="mt-3 rounded-2xl bg-[#16161e]/80 border border-iris-500/15 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/4 bg-iris-500/3">
                <BarChart2 size={14} className="text-iris-400" />
                <span className="text-[13px] font-semibold text-iris-300">{t('dashboardMain.poll.title', "Poll")}</span>
                <span className="text-[11px] text-[#9a9aac] ml-auto">{t('dashboardMain.poll.optionsCount', { defaultValue: "{count}/4 options", count: pollOptions.length })}</span>
              </div>
              <div className="p-3 space-y-2">
                {pollOptions.map((opt, i) => (
                  <div key={i} className="group rounded-xl bg-white/3 border border-white/6 hover:border-iris-500/20 focus-within:border-iris-500/30 transition-colors">
                    <div className="flex items-center gap-2">
                      <div className="pl-3 pr-1 py-2.5 text-[#4e4e60]">
                        <div className="w-5 h-5 rounded-full border-2 border-white/10 flex items-center justify-center text-[10px] font-bold text-[#9a9aac]">
                          {i + 1}
                        </div>
                      </div>
                      <input
                        value={opt}
                        onChange={(e) => {
                          const updated = [...pollOptions];
                          updated[i] = e.target.value;
                          setPollOptions(updated);
                        }}
                        placeholder={i < 2 ? t('dashboardMain.poll.optionRequired', { defaultValue: "Option {number} (required)", number: i + 1 }) : t('dashboardMain.poll.optionN', { defaultValue: "Option {number}", number: i + 1 })}
                        maxLength={80}
                        className="flex-1 bg-transparent py-2.5 text-[13px] text-white placeholder:text-[#4e4e60] focus:outline-hidden"
                      />
                      {opt.length > 0 && (
                        <span className="text-[10px] text-[#3e3e50] pr-1 tabular-nums">{opt.length}/80</span>
                      )}
                      <label className="p-2 text-[#4e4e60] hover:text-iris-400 cursor-pointer transition-colors">
                        <ImagePlus size={14} />
                        <input
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const token = getAuthToken();
                            if (!token) return;
                            const fd = new FormData();
                            fd.append('files', file);
                            try {
                              const res = await fetch(`${API_URL}/scoops/upload`, {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}` },
                                body: fd,
                              });
                              if (res.ok) {
                                const data = await res.json();
                                const url = data.urls?.[0];
                                if (url) {
                                  setPollOptionImages(prev => {
                                    const updated = [...prev];
                                    updated[i] = url;
                                    return updated;
                                  });
                                }
                              } else {
                                const err = await res.json().catch(() => ({ error: t('dashboardMain.errors.uploadFailed', "Upload failed") }));
                                setUploadError(err.code === 'nsfw_blocked' ? t('dashboardMain.errors.nsfwBlocked', "This image was rejected because it violates our content policy. Please upload appropriate content.") : (err.error || t('dashboardMain.errors.imageUploadFailed', "Image upload failed")));
                              }
                            } catch {}
                            e.target.value = '';
                          }}
                        />
                      </label>
                      {pollOptions.length > 2 && (
                        <button
                          onClick={() => {
                            setPollOptions(prev => prev.filter((_, idx) => idx !== i));
                            setPollOptionImages(prev => prev.filter((_, idx) => idx !== i));
                          }}
                          className="p-2 text-[#4e4e60] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <X size={13} />
                        </button>
                      )}
                    </div>
                    {pollOptionImages[i] && (
                      <div className="px-3 pb-2">
                        <div className="relative inline-block rounded-lg overflow-hidden border border-white/6">
                          <img src={pollOptionImages[i]!} alt="" className="h-16 w-24 object-cover" />
                          <button
                            onClick={() => setPollOptionImages(prev => { const u = [...prev]; u[i] = null; return u; })}
                            className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 text-white/70 hover:text-white"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {pollOptions.length < 4 && (
                  <button
                    onClick={() => {
                      setPollOptions(prev => [...prev, '']);
                      setPollOptionImages(prev => [...prev, null]);
                    }}
                    className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-white/8 hover:border-iris-500/30 hover:bg-iris-500/3 text-[13px] text-[#9a9aac] hover:text-iris-400 transition-all"
                  >
                    <span className="text-lg leading-none">+</span>
                    {t('dashboardMain.poll.addOption', "Add option")}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-4 px-4 py-2.5 border-t border-white/4 bg-white/1">
                <label className="flex items-center gap-2 cursor-pointer group/check">
                  <div className={`w-4 h-4 rounded-md border-2 flex items-center justify-center transition-colors ${pollMultiVote ? 'bg-iris-500 border-iris-500' : 'border-white/20 group-hover/check:border-iris-400/50'}`}>
                    {pollMultiVote && <Check size={10} className="text-white" strokeWidth={3} />}
                  </div>
                  <input type="checkbox" checked={pollMultiVote} onChange={(e) => setPollMultiVote(e.target.checked)} className="sr-only" />
                  <span className="text-[12px] text-[#a0a0b0] group-hover/check:text-white transition-colors">{t('dashboardMain.poll.multipleChoice', "Multiple choice")}</span>
                </label>
                <div className="flex items-center gap-2 ml-auto">
                  <Clock size={12} className="text-[#9a9aac]" />
                  <select
                    value={pollExpiresAt ? 'custom' : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') { setPollExpiresAt(''); return; }
                      if (val === 'custom') return;
                      const d = new Date();
                      d.setHours(d.getHours() + parseInt(val));
                      setPollExpiresAt(d.toISOString().slice(0, 16));
                    }}
                    className="bg-white/4 border border-white/6 rounded-lg px-2 py-1 text-[12px] text-[#a0a0b0] focus:outline-hidden focus:border-iris-500/40 appearance-none cursor-pointer"
                  >
                    <option value="">{t('dashboardMain.poll.noTimeLimit', "No time limit")}</option>
                    <option value="1">{t('dashboardMain.poll.oneHour', "1 hour")}</option>
                    <option value="6">{t('dashboardMain.poll.sixHours', "6 hours")}</option>
                    <option value="24">{t('dashboardMain.poll.oneDay', "1 day")}</option>
                    <option value="72">{t('dashboardMain.poll.threeDays', "3 days")}</option>
                    <option value="168">{t('dashboardMain.poll.sevenDays', "7 days")}</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Event details */}
          {postType === 'event' && (
            <div className="mt-2 space-y-2 p-3 rounded-xl bg-orange-500/4 border border-orange-500/10">
              <p className="text-[12px] text-orange-400 uppercase tracking-wider font-medium">{t('dashboardMain.event.detailsTitle', "Event details")}</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[12px] text-[#9a9aac] mb-0.5 block">{t('dashboardMain.event.starts', "Starts")}</label>
                  <DateTimePicker
                    value={eventStartsAt}
                    onChange={setEventStartsAt}
                    min={new Date().toISOString().slice(0, 16)}
                    placeholder={t('dashboardMain.event.starts', "Starts")}
                  />
                </div>
                <div>
                  <label className="text-[12px] text-[#9a9aac] mb-0.5 block">{t('dashboardMain.event.endsOptional', "Ends (optional)")}</label>
                  <DateTimePicker
                    value={eventEndsAt}
                    onChange={setEventEndsAt}
                    min={eventStartsAt || new Date().toISOString().slice(0, 16)}
                    placeholder={t('dashboardMain.event.endsOptional', "Ends (optional)")}
                    clearable
                  />
                </div>
              </div>
              <div>
                <label className="text-[12px] text-[#9a9aac] mb-0.5 block">{t('dashboardMain.event.locationUrlOptional', "Location URL (optional)")}</label>
                <input
                  type="url"
                  value={eventLocationUrl}
                  onChange={(e) => setEventLocationUrl(e.target.value)}
                  placeholder={t('dashboardMain.event.locationUrlPlaceholder', "https://zoom.us/j/... or maps link")}
                  className="w-full bg-white/4 border border-white/6 rounded-lg px-2 py-1.5 text-xs text-white placeholder:text-[#4e4e60] focus:outline-hidden focus:border-orange-500/40"
                />
              </div>
            </div>
          )}

          {/* Story options */}
          {postType === 'story' && userHubs.length > 0 && (
            <div className="mt-2 space-y-2 p-3 rounded-xl bg-pink-500/4 border border-pink-500/10">
              <div className="flex items-center gap-2">
                <Clock size={14} className="text-pink-400" />
                <p className="text-[12px] text-pink-400 uppercase tracking-wider font-medium">{t('stories.createStory', "Create Story")}</p>
                <span className="text-[11px] text-[#9a9aac] ml-auto">{t('stories.expiresIn24h', "Expires in 24 hours")}</span>
              </div>
              {userHubs.length > 1 && (
                <div>
                  <label className="text-[12px] text-[#9a9aac] mb-0.5 block">{t('stories.pickHub', "Post story to...")}</label>
                  <select
                    value={storyHubId}
                    onChange={(e) => setStoryHubId(e.target.value)}
                    className="w-full bg-white/4 border border-white/6 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-hidden focus:border-pink-500/40"
                  >
                    {userHubs.map(h => (
                      <option key={h.id} value={h.id} className="bg-[#16161e]">{h.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <p className="text-[11px] text-[#9a9aac]">{t('stories.composerHint', "Stories disappear after 24 hours. Share a quick update, behind-the-scenes moment, or announcement.")}</p>
            </div>
          )}

          {/* Tags */}
          {(showTagInput || composerTags.length > 0) && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 p-2 rounded-lg bg-white/2 border border-white/4">
              {composerTags.map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-iris-500/15 text-iris-300 text-xs group cursor-default"
                >
                  #{tag}
                  <button
                    onClick={() => removeTag(tag)}
                    className="text-iris-400/50 hover:text-iris-300 transition-colors"
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
              <input
                ref={tagInputRef}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value.replace(/\s/g, ''))}
                onKeyDown={handleTagKeyDown}
                onBlur={() => { if (tagInput.trim()) addTag(tagInput); }}
                placeholder={composerTags.length === 0 ? t('composer.addTags', "Add tags") : ''}
                className="flex-1 min-w-[80px] bg-transparent text-xs text-white placeholder:text-[#4e4e60] focus:outline-hidden"
              />
              {composerTags.length > 0 && (
                <button
                  onClick={() => { setShowTagInput(false); setComposerTags([]); setTagInput(''); }}
                  className="p-0.5 rounded text-[#9a9aac] hover:text-white transition-colors ml-auto"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Toolbar */}
        {/* Same mobile defect as FullComposer's bar: the left-hand tool row is
            wider than a phone, which pushed the Post button outside the
            composer card's overflow-hidden box and made posting impossible on
            mobile. Tools scroll; the action group wraps to its own line. */}
        <div className="flex flex-wrap items-center justify-between gap-y-2 px-4 py-2 border-t border-white/4">
          <div className="flex items-center gap-0.5 min-w-0 overflow-x-auto scrollbar-none">
            <button
              onClick={() => { setShowUrlInput(!showUrlInput); if (showUrlInput) { setComposerUrl(''); setLinkPreview(null); } }}
              className={`p-2 rounded-lg text-xs transition-colors ${showUrlInput ? 'bg-iris-500/15 text-iris-400' : 'text-[#9a9aac] hover:text-white hover:bg-white/5'}`}
              title={t('composer.addLink', "Add link")}
            >
              <Link2 size={16} />
            </button>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm,video/x-m4v"
              multiple
              onChange={handleImageSelect}
              className="hidden"
            />
            <button
              onClick={() => composerImages.length < 4 ? imageInputRef.current?.click() : undefined}
              className={`p-2 rounded-lg text-xs transition-colors ${composerImages.length > 0 ? 'bg-emerald-500/15 text-emerald-400' : 'text-[#9a9aac] hover:text-white hover:bg-white/5'} ${composerImages.length >= 4 ? 'opacity-40 cursor-not-allowed' : ''}`}
              title={composerImages.length >= 4 ? t('composer.maxImages', "Max 4 images") : t('composer.addImage', "Add image")}
            >
              <ImagePlus size={16} />
            </button>
            <button
              onClick={() => (composerImages.length + preUploadedUrls.length) < 4 ? setShowLibraryPicker(true) : undefined}
              className={`p-2 rounded-lg text-xs transition-colors text-[#9a9aac] hover:text-white hover:bg-white/5 ${(composerImages.length + preUploadedUrls.length) >= 4 ? 'opacity-40 cursor-not-allowed' : ''}`}
              title={t('fullComposer.chooseFromLibrary', 'Choose from Library')}
            >
              <Library size={16} />
            </button>
            <button
              onClick={() => setShowCamera(true)}
              className={`p-2 rounded-lg text-xs transition-colors text-[#9a9aac] hover:text-white hover:bg-white/5 ${composerImages.length >= 4 ? 'opacity-40 cursor-not-allowed' : ''}`}
              title={t('camera.photo', "Photo")}
            >
              <Camera size={16} />
            </button>
            <div>
              <button
                onClick={() => setShowGifPicker(!showGifPicker)}
                className={`p-2 rounded-lg text-xs font-bold transition-colors ${showGifPicker ? 'bg-iris-500/15 text-iris-400' : 'text-[#9a9aac] hover:text-white hover:bg-white/5'}`}
                title="GIF"
              >
                <span className="text-[11px] font-bold leading-none">GIF</span>
              </button>
              {showGifPicker && createPortal((
                // Portal to body so position:fixed escapes the dashboard
                // PageTransition transform — without it the picker
                // rendered way down the page (same bug we hit on
                // FullComposer's GIF picker; this is a separate code
                // path that I missed in the first round).
                <div className="fixed inset-0 z-70 flex items-center justify-center p-4">
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-xs" onClick={() => setShowGifPicker(false)} />
                  <div className="relative">
                    <GifPicker
                      onSelect={async (gif) => {
                        setShowGifPicker(false);
                        if (composerImages.length >= 4) return;
                        try {
                          const res = await fetch(gif.url);
                          const blob = await res.blob();
                          const file = new File([blob], `gif-${Date.now()}.gif`, { type: 'image/gif' });
                          setComposerImages(prev => [...prev, file]);
                          setComposerImagePreviews(prev => [...prev, gif.preview]);
                        } catch {
                          setComposerImagePreviews(prev => [...prev, gif.url]);
                        }
                      }}
                      onClose={() => setShowGifPicker(false)}
                    />
                  </div>
                </div>
              ), document.body)}
            </div>
            <button
              onClick={() => setShowMediaStudio(true)}
              className="p-2 rounded-lg text-xs transition-colors text-[#9a9aac] hover:text-white hover:bg-white/5"
              title={t('composer.mediaStudio', "Media Studio")}
            >
              <Wand2 size={16} />
            </button>
            <button
              onClick={() => setShowTemplates(true)}
              className="p-2 rounded-lg text-xs transition-colors text-[#9a9aac] hover:text-white hover:bg-white/5"
              title={t('templates.buttonTooltip', "Post templates")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></svg>
            </button>
            <button
              onClick={() => { setShowTagInput(!showTagInput); if (!showTagInput) setTimeout(() => tagInputRef.current?.focus(), 50); }}
              className={`p-2 rounded-lg text-xs transition-colors ${showTagInput || composerTags.length > 0 ? 'bg-iris-500/15 text-iris-400' : 'text-[#9a9aac] hover:text-white hover:bg-white/5'}`}
              title={t('dashboardMain.composer.addTagsTitle', "Add tags")}
            >
              <Hash size={16} />
            </button>
            <button
              onClick={() => {
                if (composerRef.current) {
                  const pos = composerRef.current.selectionStart;
                  const before = composerText.slice(0, pos);
                  const after = composerText.slice(pos);
                  const newText = before + '@' + after;
                  setComposerText(newText);
                  setTimeout(() => {
                    composerRef.current?.focus();
                    composerRef.current?.setSelectionRange(pos + 1, pos + 1);
                    detectMention(newText, composerRef.current, setMentionQuery, setMentionPos);
                  }, 0);
                }
              }}
              className={`p-2 rounded-lg text-xs transition-colors ${mentionQuery !== null ? 'bg-blue-500/15 text-blue-400' : 'text-[#9a9aac] hover:text-white hover:bg-white/5'}`}
              title={t('composer.mentionSomeone', "Mention someone")}
            >
              <AtSign size={16} />
            </button>
            {hubTiers.length > 0 && (
              <>
                <div className="w-px h-4 bg-white/6 mx-1" />
                <button
                  onClick={() => { setIsGated(!isGated); if (!isGated && hubTiers.length > 0 && !gateTierId) setGateTierId(hubTiers[0].id); }}
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${
                    isGated ? 'bg-amber-500/15 text-amber-400' : 'text-[#9a9aac] hover:text-white hover:bg-white/5'
                  }`}
                  title={t('composer.makeSubscribersOnly', "Make subscribers only")}
                >
                  <Lock size={12} />
                  <span className="hidden sm:inline">{isGated ? t('composer.gated', "Gated") : t('composer.gate', "Gate")}</span>
                </button>
                {isGated && hubTiers.length > 1 && (
                  <select
                    value={gateTierId}
                    onChange={e => setGateTierId(e.target.value)}
                    className="bg-white/4 border border-white/10 rounded-lg px-1.5 py-0.5 text-[12px] text-white outline-hidden"
                  >
                    {hubTiers.map(t => (
                      <option key={t.id} value={t.id}>{t.name} (${(t.price_monthly / 100).toFixed(0)}/mo)</option>
                    ))}
                  </select>
                )}
              </>
            )}
            <div className="w-px h-4 bg-white/6 mx-1" />
            <span className="text-[12px] text-[#4e4e60] select-none">
              {composerText.length > 0 && `${composerText.length}`}
            </span>
          </div>

          <div className="flex items-center gap-2 shrink-0 ms-auto">
            <span className="text-[12px] text-[#4e4e60] hidden sm:block">
              {(composerText.trim() || composerUrl.trim() || composerImages.length > 0) ? t('composer.cmdEnterToPost', "⌘ Enter to post") : ''}
            </span>
            <div className="relative">
              <button
                onClick={() => setShowSchedulePicker(!showSchedulePicker)}
                className={`p-1.5 rounded-lg transition-colors ${
                  scheduledAt ? 'text-orange-400 bg-orange-500/15' : 'text-[#9a9aac] hover:text-white hover:bg-white/5'
                }`}
                title={scheduledAt ? t('dashboardMain.composer.scheduledLabel', { defaultValue: "Scheduled: {date}", date: formatDateTime(scheduledAt) }) : t('composer.schedulePost', "Schedule post")}
              >
                <Clock size={14} />
              </button>
              {showSchedulePicker && (
                <div className="absolute bottom-full inset-e-0 mb-1 p-3 bg-[#16161e] border border-white/10 rounded-xl shadow-xl z-50 w-[280px]">
                  <p className="text-[12px] text-[#9a9aac] uppercase tracking-wider mb-2">{t('composer.schedulePost', "Schedule post")}</p>
                  <DateTimePicker
                    value={scheduledAt}
                    onChange={setScheduledAt}
                    min={new Date().toISOString().slice(0, 16)}
                    placeholder={t('composer.schedulePost', "Schedule post")}
                    clearable
                  />
                  {scheduledAt && (
                    <button
                      onClick={() => { setScheduledAt(''); setShowSchedulePicker(false); }}
                      className="w-full py-1 mt-2 rounded-lg text-xs text-[#9a9aac] hover:text-white hover:bg-white/5 transition-colors"
                    >
                      {t('composer.clearSchedule', "Clear schedule")}
                    </button>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={handlePost}
              disabled={posting || uploading || (!composerText.trim() && !composerUrl.trim() && composerImages.length === 0 && preUploadedUrls.length === 0 && postType !== 'poll' && postType !== 'event' && postType !== 'story' && postType !== 'status')}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-linear-to-r/srgb from-iris-600 via-iris-500 to-iris-500 text-[#0C0C12] text-sm font-semibold hover:from-iris-500 hover:via-iris-400 hover:to-iris-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all disabled:bg-none disabled:bg-[#2a2a3a] glow-primary"
            >
              {(posting || uploading) ? <Loader2 size={14} className="animate-spin" /> : scheduledAt ? <Clock size={14} /> : <Send size={14} />}
              {uploading ? t('composer.uploading', "Uploading...") : scheduledAt ? t('composer.schedule', "Schedule") : t('composer.post', "Post")}
            </button>
          </div>
        </div>
      </div>

      {/* Draft Nudges */}
      <DraftNudgeBanner />

      {/* Feed */}
      {feedError && <div role="alert" className="mb-4 rounded-xl border border-amber-500/30 p-4 text-sm">
        {feedError}
        <button onClick={() => fetchScoops(true)} className="ms-3 underline">{t('common.retry', 'Try again')}</button>
      </div>}
      {loading ? (
        <FeedSkeleton count={4} />
      ) : feedError && scoops.length === 0 ? null : scoops.length === 0 ? (
        <div className="text-center py-16">
          <div className="relative w-20 h-20 mx-auto mb-5">
            <div className="absolute inset-0 bg-linear-to-br/srgb from-iris-500/30 via-iris-500/20 to-pink-500/15 blur-2xl rounded-full" />
            <div className="relative w-full h-full rounded-2xl bg-linear-to-br/srgb from-iris-500/15 to-iris-500/10 border border-white/6 flex items-center justify-center">
              <Globe size={32} className="text-iris-300" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">
            {activeTab === 'for-you' ? t('dashboardMain.feed.caughtUpTitle', 'You’re caught up')
              : activeTab === 'following' ? t('dashboardMain.feed.emptyFollowingTitle', "Your feed is empty")
              : activeTab === 'friends' ? t('dashboardMain.feed.emptyFriendsTitle', 'No friend posts yet')
              : activeTab === 'agents' ? t('dashboardMain.feed.emptyAgentsTitle', 'No agent posts yet')
              : t('dashboardMain.feed.emptyDiscoverTitle', "Nothing to discover yet")}
          </h3>
          <p className="text-sm text-[#9a9aac] max-w-sm mx-auto">
            {activeTab === 'for-you'
              ? t('dashboardMain.feed.caughtUpDescription', 'There are no more posts in your content languages right now. You can update your languages and interests in settings.')
              : activeTab === 'following'
              ? t('dashboardMain.feed.emptyFollowingDescription', "Follow people and communities to see their posts here, or share your first post above.")
              : activeTab === 'friends'
              ? t('dashboardMain.feed.emptyFriendsDescription', 'Add friends to see their posts here')
              : activeTab === 'agents'
              ? t('dashboardMain.feed.emptyAgentsDescription', 'Browse public agents and follow some — their AI-drafted posts will show up here. ')
              : t('dashboardMain.feed.emptyDiscoverDescription', "Be the first to post! Share a link or thought above.")}
          </p>
          {activeTab === 'agents' && (
            <a
              href="/dashboard/agents"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-iris-600 hover:bg-iris-500 text-[#0C0C12] text-sm font-medium focus-visible:ring-2 focus-visible:ring-iris-500/60 focus-visible:outline-hidden"
            >
              {t('dashboardMain.feed.browseAgentsCta', 'Browse public agents')}
            </a>
          )}
          {activeTab === 'friends' && (
            <a
              href="/dashboard/friends"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-iris-600 hover:bg-iris-500 text-[#0C0C12] text-sm font-medium focus-visible:ring-2 focus-visible:ring-iris-500/60 focus-visible:outline-hidden"
            >
              {t('dashboardMain.feed.findFriendsCta', 'Find friends')}
            </a>
          )}
          {activeTab === 'following' && (
            <a
              href="/dashboard/discover"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-iris-600 hover:bg-iris-500 text-[#0C0C12] text-sm font-medium focus-visible:ring-2 focus-visible:ring-iris-500/60 focus-visible:outline-hidden"
            >
              {t('dashboardMain.feed.discoverCta', 'Discover hubs and people')}
            </a>
          )}
        </div>
      ) : (
        <div className={designV2 ? '' : 'space-y-3'}>
          {scoops.filter(s => s && s.id).map((scoop) => (
            <React.Fragment key={scoop.id}>
            {scoop.id === overflowStartId && (
              <div role="separator" className="flex items-center gap-3 py-4 text-xs text-[#9a9aac]" data-testid="feed-overflow-divider">
                <span className="h-px flex-1 bg-white/10" />
                <span className="text-center">{t('dashboardMain.feed.caughtUpContinue', 'You’re caught up on For You. More posts from Discover:')}</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>
            )}
            <div
              data-scoop-id={scoop.id}
              onTimeUpdateCapture={event=>recordVideo(event,scoop.id)}
              onEndedCapture={event=>recordVideo(event,scoop.id)}
              ref={(el) => {
                if (el && observerRef.current) {
                  observerRef.current.observe(el);
                }
              }}
            >
            <ScoopCard
              scoop={scoop}
              onLike={handleLike}
              onScoopVote={handleScoopVote}
              onReaction={handleReaction}
              onTip={handleTip}
              onDelete={handleDelete}
              onReply={handleReply}
              onReshare={handleReshare}
              onEdit={handleEdit}
              onReport={handleReport}
              onFeedFeedback={activeTab==='for-you'?handleFeedFeedback:undefined}
              onSave={activeTab==='for-you'?handleSave:undefined}
              formatTime={formatTime}
              getAuthorDisplayName={getAuthorDisplayName}
              reactions={scoopReactions[scoop.id] || []}
              reactionPickerOpen={showReactionPicker === scoop.id}
              onToggleReactionPicker={() => setShowReactionPicker(prev => prev === scoop.id ? null : scoop.id)}
              authorBadges={userBadges[scoop.user_id] || []}
              designV2={designV2}
            />
            </div>
            </React.Fragment>
          ))}

          {/* Infinite scroll sentinel */}
          <div ref={loadMoreRef} className="h-4" />
          {loadingMore && (
            <div className="flex justify-center py-4">
              <Loader2 size={20} className="animate-spin text-[#9a9aac]" />
            </div>
          )}
        </div>
      )}

      {/* Content Templates */}
      <ContentTemplates
        isOpen={showTemplates}
        onClose={() => setShowTemplates(false)}
        onSelect={(text, tags) => {
          setComposerText(text);
          if (tags?.length) setComposerTags(prev => [...new Set([...prev, ...tags])]);
          composerRef.current?.focus();
        }}
      />

      {/* Media Library Picker */}
      {showLibraryPicker && createPortal(
        <MediaLibraryPicker
          remaining={4 - (composerImages.length + preUploadedUrls.length)}
          onClose={() => setShowLibraryPicker(false)}
          onSelect={insertLibraryAssets}
        />,
        document.body,
      )}

      {/* Media Studio Modal */}
      <MediaStudioPanel
        isOpen={showMediaStudio}
        onClose={() => setShowMediaStudio(false)}
        onInsertImage={(url, attribution) => { handleInsertMedia(url, attribution); setShowMediaStudio(false); }}
        authToken={getAuthToken() || ''}
      />

      {/* Image Editor Modal */}
      <ImageEditor
        isOpen={showImageEditor}
        onClose={() => { setShowImageEditor(false); setImageToEdit(null); }}
        imageFile={imageToEdit?.file || null}
        onSave={(editedFile) => {
          if (imageToEdit !== null) {
            const idx = imageToEdit.index;
            setComposerImages(prev => prev.map((f, i) => i === idx ? editedFile : f));
            const reader = new FileReader();
            reader.onload = () => setComposerImagePreviews(prev => prev.map((p, i) => i === idx ? (reader.result as string) : p));
            reader.readAsDataURL(editedFile);
          }
          setImageToEdit(null);
        }}
      />
      {uploadError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-xs" onClick={() => setUploadError(null)} />
          <div className="relative w-full max-w-sm bg-[#16161e] border border-white/10 rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-500/15 flex items-center justify-center">
                <ShieldAlert size={24} className="text-red-400" />
              </div>
              <h3 className="text-sm font-semibold text-white mb-2">{t('dashboardMain.errors.contentPolicyViolation', "Content policy violation")}</h3>
              <p className="text-xs text-[#8e8ea0] mb-5">{uploadError}</p>
              <button
                onClick={() => setUploadError(null)}
                className="px-5 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camera capture modal */}
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            if (composerImages.length >= 4) return;
            setComposerImages(prev => [...prev, file]);
            const reader = new FileReader();
            reader.onload = () => setComposerImagePreviews(prev => [...prev, reader.result as string]);
            reader.readAsDataURL(file);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
}

function detectMention(
  text: string,
  textarea: HTMLTextAreaElement | null,
  setQuery: (q: string | null) => void,
  setPos: (p: { top: number; left: number }) => void,
) {
  if (!textarea) { setQuery(null); return; }
  const cursor = textarea.selectionStart;
  const before = text.slice(0, cursor);
  const match = before.match(/@(\w*)$/);
  if (match) {
    setQuery(match[1]);
    const rect = textarea.getBoundingClientRect();
    const parentRect = textarea.offsetParent?.getBoundingClientRect() || rect;
    setPos({ top: rect.bottom - parentRect.top + 4, left: rect.left - parentRect.left });
  } else {
    setQuery(null);
  }
}

function handleMentionSelect(
  user: MentionUser,
  text: string,
  setText: (t: string) => void,
  textarea: HTMLTextAreaElement | null,
  mentions: MentionData[],
  setMentions: (m: MentionData[]) => void,
  setQuery: (q: string | null) => void,
) {
  if (!textarea) return;
  const cursor = textarea.selectionStart;
  const before = text.slice(0, cursor);
  const after = text.slice(cursor);
  const atIndex = before.lastIndexOf('@');
  const newText = before.slice(0, atIndex) + `@${user.displayName} ` + after;
  setText(newText);
  setMentions([...mentions, { userId: user.id, username: user.username, displayName: user.displayName }]);
  setQuery(null);
  setTimeout(() => {
    const newCursor = atIndex + user.displayName.length + 2;
    textarea.focus();
    textarea.setSelectionRange(newCursor, newCursor);
  }, 0);
}

function renderTextWithMentions(text: string, trackedLinksMap?: Record<string, string>) {
  const urlRegex = /(https?:\/\/[^\s<>"')\]]+)/g;
  const mentionRegex = /(@\w[\w]*)/g;

  const urlParts = text.split(urlRegex);
  return urlParts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      const isInternal = part.includes('soltik.app');
      const display = (() => {
        try {
          const u = new URL(part);
          return u.hostname.replace('www.', '') + (u.pathname.length > 1 ? u.pathname.slice(0, 30) + (u.pathname.length > 30 ? '...' : '') : '');
        } catch { return part.slice(0, 50); }
      })();
      // Use server-provided shortcode, fall back to trackedUrl()
      const href = isInternal ? part : (trackedLinksMap?.[part] || trackedUrl(part));
      return (
        <a key={i} href={href} target="_blank" rel="noopener noreferrer"
          className="text-iris-400 hover:text-iris-300 hover:underline transition-colors">
          {display}
        </a>
      );
    }
    const mentionParts = part.split(mentionRegex);
    return mentionParts.map((mp, j) => {
      if (mp.startsWith('@') && mp.length > 1) {
        return <span key={`${i}-${j}`} className="text-iris-400 font-medium">{mp}</span>;
      }
      return <React.Fragment key={`${i}-${j}`}>{mp}</React.Fragment>;
    });
  });
}

function PollCard({ scoopId }: { scoopId: string }) {
  const { t } = useTranslation();
  const [poll, setPoll] = useState<PollData | null>(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [justVoted, setJustVoted] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    fetch(`${API_URL}/scoops/${scoopId}/poll`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setPoll(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [scoopId]);

  const handleVote = async (optionIndex: number) => {
    const token = getAuthToken();
    if (!token || !poll || voting) return;
    if (poll.viewer_votes.length > 0 && !poll.multi_vote) return;
    setVoting(true);
    try {
      const res = await fetch(`${API_URL}/scoops/${scoopId}/poll/vote`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ option_index: optionIndex }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.poll) {
          setPoll(data.poll);
          setJustVoted(true);
        }
      }
    } finally {
      setVoting(false);
    }
  };

  if (loading) return (
    <div className="mb-3 space-y-2">
      {[1, 2, 3].map(i => (
        <div key={i} className="h-11 rounded-xl bg-white/3 animate-pulse" />
      ))}
    </div>
  );
  if (!poll) return null;

  const hasVoted = poll.viewer_votes.length > 0;
  const showResults = hasVoted || poll.expired;
  const maxVotes = Math.max(...poll.votes_by_option, 0);
  const winnerIndex = poll.votes_by_option.indexOf(maxVotes);
  const hasWinner = poll.total_votes > 0 && poll.votes_by_option.filter(v => v === maxVotes).length === 1;
  const hasImages = poll.option_images && poll.option_images.some(img => img);

  // Time remaining helper
  const getTimeRemaining = () => {
    if (!poll.expires_at) return null;
    const now = Date.now();
    const end = new Date(poll.expires_at).getTime();
    const diff = end - now;
    if (diff <= 0) return t('dashboardMain.dashboardMain.mention.finalResults', "Final results");
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(hours / 24);
    if (days > 0) return t('dashboardMain.dashboardMain.mention.daysHoursLeft', { defaultValue: "{days}d {hours}h left", days, hours: hours % 24 });
    if (hours > 0) return t('dashboardMain.dashboardMain.mention.hoursLeft', { defaultValue: "{hours}h left", hours });
    const mins = Math.floor(diff / 60000);
    return t('dashboardMain.dashboardMain.mention.minutesLeft', { defaultValue: "{minutes}m left", minutes: mins });
  };

  return (
    <div className="mb-3">
      <div className="space-y-2">
        {poll.options.map((option: string, i: number) => {
          const votes = poll.votes_by_option[i] || 0;
          const pct = poll.total_votes > 0 ? Math.round((votes / poll.total_votes) * 100) : 0;
          const isSelected = poll.viewer_votes.includes(i);
          const isWinner = hasWinner && i === winnerIndex;

          const optImg = poll.option_images?.[i];

          if (!showResults) {
            // Pre-vote: clean clickable options
            return (
              <button
                key={i}
                onClick={() => handleVote(i)}
                disabled={voting}
                className={`relative w-full text-left rounded-xl border border-white/8 hover:border-iris-500/40 hover:bg-iris-500/4 transition-all duration-200 group disabled:opacity-60 overflow-hidden ${hasImages ? '' : ''}`}
              >
                {optImg && (
                  <div className="w-full h-28 bg-white/2">
                    <img src={optImg} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex items-center px-4 py-3">
                  <div className="w-5 h-5 rounded-full border-2 border-white/20 group-hover:border-iris-400/60 me-3 flex items-center justify-center transition-colors shrink-0">
                    {voting && <Loader2 size={10} className="animate-spin text-iris-400" />}
                  </div>
                  <span className="text-[14px] text-[#e0e0f0] group-hover:text-white transition-colors">{option}</span>
                </div>
              </button>
            );
          }

          // Post-vote: X/Twitter-style bars
          return (
            <div key={i} className="relative rounded-xl overflow-hidden">
              <div
                className={`absolute inset-y-0 ltr:left-0 rtl:right-0 rounded-xl transition-all ${justVoted ? 'duration-700' : 'duration-300'} ease-out ${
                  isWinner && (poll.expired || hasVoted)
                    ? 'bg-iris-500/20'
                    : isSelected
                    ? 'bg-iris-500/12'
                    : 'bg-white/4'
                }`}
                style={{
                  width: `${pct}%`,
                  transitionDelay: justVoted ? `${i * 80}ms` : '0ms',
                }}
              />
              <div className="relative flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  {isSelected && (
                    <div className="w-[18px] h-[18px] rounded-full bg-iris-500 flex items-center justify-center shrink-0">
                      <Check size={11} className="text-white" strokeWidth={3} />
                    </div>
                  )}
                  {optImg && (
                    <img src={optImg} alt="" className="w-8 h-8 rounded-md object-cover shrink-0" />
                  )}
                  <span className={`text-[14px] truncate ${
                    isSelected ? 'text-white font-medium' :
                    isWinner ? 'text-[#e0e0f0] font-medium' :
                    'text-[#b0b0c0]'
                  }`}>
                    {option}
                  </span>
                </div>
                <span className={`text-[14px] ms-3 tabular-nums shrink-0 ${
                  isWinner ? 'text-white font-bold' :
                  isSelected ? 'text-iris-300 font-semibold' :
                  'text-[#9a9aac] font-medium'
                }`}>
                  {pct}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center gap-1.5 mt-2.5 px-1">
        <span className="text-[13px] text-[#9a9aac]">
          {t('dashboardMain.dashboardMain.mention.votes', { defaultValue: "{count} votes", count: poll.total_votes })}
        </span>
        {poll.multi_vote && (
          <>
            <span className="text-[#3a3a4a]">·</span>
            <span className="text-[13px] text-[#9a9aac]">{t('dashboardMain.dashboardMain.mention.multipleChoice', "Multiple choice")}</span>
          </>
        )}
        {(poll.expired || poll.expires_at) && (
          <>
            <span className="text-[#3a3a4a]">·</span>
            <span className={`text-[13px] ${poll.expired ? 'text-[#9a9aac]' : 'text-[#8e8ea0]'}`}>
              {poll.expired ? t('dashboardMain.dashboardMain.mention.finalResults', "Final results") : getTimeRemaining()}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function EventCard({ scoopId }: { scoopId: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [event, setEvent] = useState<EventData | null>(null);
  const [loading, setLoading] = useState(true);
  const [rsvping, setRsvping] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    fetch(`${API_URL}/scoops/${scoopId}/event`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setEvent(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [scoopId]);

  const handleRsvp = async (status: string) => {
    const token = getAuthToken();
    if (!token || rsvping) return;
    setRsvping(true);
    try {
      const res = await fetch(`${API_URL}/scoops/${scoopId}/rsvp`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.event) setEvent(data.event);
      }
    } finally {
      setRsvping(false);
    }
  };

  if (loading) return <div className="flex justify-center py-3"><Loader2 size={14} className="animate-spin text-[#9a9aac]" /></div>;
  if (!event) return null;

  const startDate = new Date(event.starts_at);
  const isPast = startDate < new Date();

  return (
    <div className="mb-3 rounded-xl border border-orange-500/10 bg-orange-500/3 overflow-hidden">
      <div className="p-3 space-y-2">
        <div className="flex items-start gap-3">
          <div className="flex flex-col items-center px-2 py-1 rounded-lg bg-orange-500/10 text-center min-w-[48px]">
            <span className="text-[12px] text-orange-400 uppercase font-medium">{startDate.toLocaleDateString(locale, { month: 'short' })}</span>
            <span className="text-lg font-bold text-white leading-none">{startDate.getDate()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-xs text-[#d0d0e0]">
              <Clock size={10} className="text-orange-400" />
              <span>
                {formatTime24(startDate)}
                {event.ends_at && ` — ${formatTime24(event.ends_at)}`}
              </span>
            </div>
            {event.location_url && (
              <a href={event.location_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-iris-400 hover:text-iris-300 mt-1 transition-colors">
                <MapPin size={10} />
                <span className="truncate">{(() => { try { return new URL(event.location_url).hostname; } catch { return t('dashboardMain.dashboardMain.mention.location', "Location"); } })()}</span>
              </a>
            )}
          </div>
        </div>
        {!isPast && (
          <div className="flex items-center gap-1.5">
            {(['going', 'maybe', 'not_going'] as const).map(status => (
              <button
                key={status}
                onClick={() => handleRsvp(status)}
                disabled={rsvping}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  event.viewer_rsvp === status
                    ? status === 'going' ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : status === 'maybe' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    : 'bg-white/4 text-[#b0b0c0] border border-white/6 hover:border-white/10'
                }`}
              >
                {status === 'going' ? t('dashboardMain.dashboardMain.mention.going', "Going") : status === 'maybe' ? t('dashboardMain.dashboardMain.mention.maybe', "Maybe") : t('dashboardMain.dashboardMain.mention.cantGo', "Can't go")}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1 text-[12px] text-[#9a9aac]">
          <Users size={10} />
          <span>{t('dashboardMain.dashboardMain.mention.attending', { defaultValue: "{count} attending", count: event.rsvp_count })}</span>
          {isPast && <span className="text-orange-400/60 ml-1">· {t('dashboardMain.dashboardMain.mention.eventEnded', "Event ended")}</span>}
        </div>
      </div>
    </div>
  );
}

function ScoopCard({
  scoop,
  onLike,
  onScoopVote,
  onReaction,
  onTip,
  onDelete,
  onReply,
  onReshare,
  onEdit,
  onReport,
  onFeedFeedback,
  onSave,
  formatTime,
  getAuthorDisplayName,
  reactions,
  reactionPickerOpen,
  onToggleReactionPicker,
  authorBadges,
  designV2: scoopDesignV2,
}: {
  onSave?: (postId:string)=>Promise<void>;
  onFeedFeedback?: (postId:string,action:'hide'|'not_interested')=>Promise<void>;
  scoop: ScoopPost;
  onLike: (id: string) => void;
  onScoopVote: (id: string) => void;
  onReaction: (id: string, emoji: string) => void;
  onTip: (id: string, amount: number, message?: string) => void;
  onDelete: (id: string) => void;
  onReply: (id: string, content: string, mentions?: MentionData[]) => Promise<boolean | undefined>;
  onReshare: (id: string, comment?: string) => void;
  onEdit: (id: string, updates: { description?: string; tags?: string[]; url?: string | null; title?: string | null; media_urls?: any[] | null; image_url?: string | null; content_type?: string; metadata?: any }) => Promise<boolean>;
  onReport?: (id: string, category: string, description?: string) => Promise<boolean>;
  formatTime: (date: string) => string;
  getAuthorDisplayName: (scoop: ScoopPost) => string;
  reactions: ScoopReaction[];
  reactionPickerOpen: boolean;
  onToggleReactionPicker: () => void;
  authorBadges?: { badge_type: string; badge_data?: any }[];
  /** V2 design flag — passed in (not hooked) so we don't pay the PostHog
   *  register cost on every card render. Hoisted to DashboardPage. */
  designV2: boolean;
}) {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState<ReplyPost[]>([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFactCheck, setShowFactCheck] = useState(false);
  const [factCheckData, setFactCheckData] = useState<{ summary: string; claims: { claim: string; verdict: string; explanation: string }[]; overall: string } | null>(null);
  const [factCheckLoading, setFactCheckLoading] = useState(false);
  const [showReshareConfirm, setShowReshareConfirm] = useState(false);
  const [reshareComment, setReshareComment] = useState('');
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipAmount, setTipAmount] = useState(300);
  const [tipMessage, setTipMessage] = useState('');
  const [tipping, setTipping] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [urlSummary, setUrlSummary] = useState<string | null>(null);
  const [urlSummaryLoading, setUrlSummaryLoading] = useState(false);
  const [urlSummaryError, setUrlSummaryError] = useState<string | null>(null);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const [replyMentions, setReplyMentions] = useState<MentionData[]>([]);
  const [replyMentionQuery, setReplyMentionQuery] = useState<string | null>(null);
  const [replyMentionPos, setReplyMentionPos] = useState({ top: 0, left: 0 });
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editTagInput, setEditTagInput] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editUrlRemoved, setEditUrlRemoved] = useState(false);
  const [editMediaUrls, setEditMediaUrls] = useState<string[]>([]);
  const [editNewImages, setEditNewImages] = useState<File[]>([]);
  const [editNewImagePreviews, setEditNewImagePreviews] = useState<string[]>([]);
  const [editUploading, setEditUploading] = useState(false);
  const [editMentionQuery, setEditMentionQuery] = useState<string | null>(null);
  const [editMentionPos, setEditMentionPos] = useState({ top: 0, left: 0 });
  const [saving, setSaving] = useState(false);
  const editRef = useRef<HTMLTextAreaElement>(null);
  const editImageInputRef = useRef<HTMLInputElement>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportCategory, setReportCategory] = useState<ReportCategory | ''>('');
  const [reportDescription, setReportDescription] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const shareUrl = `https://soltik.app/s/${scoop.id}`;
  const shareTitle = scoop.title || scoop.description?.slice(0, 100) || t('dashboardMain.card.checkOutPost', "Check out this post");

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {}
    setShowShareMenu(false);
  };

  const handleShareX = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    setShowShareMenu(false);
  };

  const handleShareLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
    setShowShareMenu(false);
  };

  const openReportModal = () => {
    setShowMenu(false);
    setShowReportModal(true);
  };

  const handleSubmitReport = async () => {
    if (!reportCategory || !onReport) return;
    setSubmittingReport(true);
    try {
      const success = await onReport(scoop.id, reportCategory, reportDescription || undefined);
      if (success) {
        setReportSubmitted(true);
        setTimeout(() => {
          setShowReportModal(false);
          setReportSubmitted(false);
          setReportCategory('');
          setReportDescription('');
        }, 2000);
      }
    } catch {
      // Handle error
    } finally {
      setSubmittingReport(false);
    }
  };

  // ── Fetch credit balance when tip modal opens ──────────────────────────
  useEffect(() => {
    if (showTipModal) {
      const token = getAuthToken();
      if (token) {
        fetch(`${API_URL}/credits`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data) setCreditBalance(data.balance || 0); })
          .catch(() => {});
      }
    }
  }, [showTipModal]);

  const fetchReplies = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    setLoadingReplies(true);
    try {
      const res = await fetch(`${API_URL}/scoops/${scoop.id}/replies?sort=best&limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setReplies(data.replies || []);
      }
    } catch {
      // silently fail
    } finally {
      setLoadingReplies(false);
    }
  }, [scoop.id]);

  const handleSummarizeUrl = async () => {
    if (!scoop.url || urlSummaryLoading) return;
    setUrlSummaryLoading(true);
    setUrlSummaryError(null);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
      const res = await fetch(`${API_URL}/cf-ai/summarize-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ url: scoop.url }),
      });
      const data = await res.json();
      if (data.success && data.summary) {
        setUrlSummary(data.summary);
      } else {
        setUrlSummaryError(data.error || 'Failed to summarize');
      }
    } catch {
      setUrlSummaryError('Failed to summarize article');
    } finally {
      setUrlSummaryLoading(false);
    }
  };

  const toggleReplies = () => {
    if (!showReplies) {
      fetchReplies();
    }
    setShowReplies(!showReplies);
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || sendingReply) return;
    setSendingReply(true);
    const success = await onReply(scoop.id, replyText, replyMentions);
    if (success) {
      setReplyText('');
      setReplyMentions([]);
      await fetchReplies();
    }
    setSendingReply(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  const handleReshareClick = () => {
    setShowReshareConfirm(true);
  };

  const confirmReshare = (comment?: string) => {
    onReshare(scoop.id, comment);
    setShowReshareConfirm(false);
    setReshareComment('');
  };

  const handleFactCheck = async () => {
    if (factCheckData) { setShowFactCheck(!showFactCheck); return; }
    setShowFactCheck(true);
    setFactCheckLoading(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_URL}/scoops/${scoop.id}/fact-check`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFactCheckData(data.factCheck);
      }
    } catch {
      // silent
    } finally {
      setFactCheckLoading(false);
    }
  };

  const getReplyAuthorName = (reply: ReplyPost) => {
    if (reply.author_name) return reply.author_name;
    if (reply.author_display_name) return reply.author_display_name;
    if (reply.author_username) return `@${reply.author_username}`;
    return t('dashboardMain.card.authorFallback', 'User');
  };

  const startEditing = () => {
    setEditText(scoop.description || '');
    setEditTags([...(scoop.tags || [])]);
    setEditTagInput('');
    setEditUrl(scoop.url || '');
    setEditUrlRemoved(false);
    setEditMediaUrls([...(scoop.media_urls || [])]);
    setEditNewImages([]);
    setEditNewImagePreviews([]);
    setIsEditing(true);
    setShowMenu(false);
    setTimeout(() => editRef.current?.focus(), 50);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditText('');
    setEditTags([]);
    setEditTagInput('');
    setEditUrl('');
    setEditUrlRemoved(false);
    setEditMediaUrls([]);
    setEditNewImages([]);
    setEditNewImagePreviews([]);
    setEditMentionQuery(null);
  };

  const saveEdit = async () => {
    if (saving) return;
    setSaving(true);

    const token = getAuthToken();
    let allMediaUrls = [...editMediaUrls];

    // Upload new images if any
    if (editNewImages.length > 0 && token) {
      setEditUploading(true);
      try {
        const formData = new FormData();
        editNewImages.forEach(f => formData.append('files', f));
        const uploadRes = await fetch(`${API_URL}/scoops/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!uploadRes.ok) {
          const err = await uploadRes.json().catch(() => ({ error: t('dashboardMain.errors.uploadFailed', "Upload failed") }));
          setUploadError(err.code === 'nsfw_blocked' ? t('dashboardMain.errors.nsfwBlocked', "This image was rejected because it violates our content policy. Please upload appropriate content.") : (err.error || t('dashboardMain.errors.imageUploadFailed', "Image upload failed")));
          setSaving(false);
          setEditUploading(false);
          return;
        }
        const uploadData = await uploadRes.json();
        allMediaUrls = [...allMediaUrls, ...(uploadData.urls || [])];
      } catch {
        setUploadError(t('dashboardMain.errors.imageUploadFailed', "Image upload failed"));
        setSaving(false);
        setEditUploading(false);
        return;
      }
      setEditUploading(false);
    }

    const updates: { description?: string; tags?: string[]; url?: string | null; title?: string | null; media_urls?: any[] | null; image_url?: string | null; content_type?: string; metadata?: any } = {
      description: editText.trim() || undefined,
      tags: editTags,
    };
    if (editUrlRemoved) {
      updates.url = null;
      updates.title = null;
      if (scoop.content_type === 'video') {
        updates.content_type = allMediaUrls.length > 0 ? 'image' : 'note';
        updates.metadata = null;
      }
    } else if (editUrl !== (scoop.url || '')) {
      updates.url = editUrl.trim() || null;
      if (updates.url) {
        const video = detectVideoUrl(updates.url);
        if (video) {
          updates.content_type = 'video';
          updates.metadata = { videoId: video.id, videoPlatform: video.platform, embedUrl: video.embedUrl, thumbnailUrl: video.thumbnailUrl };
        } else if (scoop.content_type === 'video') {
          updates.content_type = 'link';
          updates.metadata = null;
        }
      } else if (scoop.content_type === 'video') {
        updates.content_type = allMediaUrls.length > 0 ? 'image' : 'note';
        updates.metadata = null;
      }
    }
    // Send media_urls if changed
    const origMedia = scoop.media_urls || [];
    if (allMediaUrls.length !== origMedia.length || allMediaUrls.some((u, i) => u !== origMedia[i])) {
      updates.media_urls = allMediaUrls.length > 0 ? allMediaUrls : null;
      if (allMediaUrls.length > 0) {
        updates.image_url = allMediaUrls[0];
      } else if (origMedia.length > 0) {
        updates.image_url = null;
      }
    }
    const success = await onEdit(scoop.id, updates);
    if (success) setIsEditing(false);
    setSaving(false);
  };

  const addEditTag = (tag: string) => {
    const cleaned = tag.trim().toLowerCase().replace(/^#/, '').replace(/[^a-z0-9_-]/g, '');
    if (cleaned && !editTags.includes(cleaned)) setEditTags(prev => [...prev, cleaned]);
    setEditTagInput('');
  };

  // V2: chromeless post per docs/design/mocks/dashboard-feed.html — no card
  // border/background; posts separated by a hairline divider. Hover affordance
  // is a subtle background instead of an outline, since the outline-on-hover
  // re-introduces the card chrome we just removed.
  const cardWrapperCls = scoopDesignV2
    ? 'border-b border-white/6 transition-colors duration-200 hover:bg-white/1.5'
    : 'rounded-2xl bg-surface-elevated border border-white/6 hover:border-iris-400/20 hover:shadow-[0_0_0_1px_rgba(129,140,248,0.12),0_8px_30px_rgba(99,102,241,0.08)] transition-all duration-200 overflow-hidden shadow-[0_1px_0_rgba(255,255,255,0.02)_inset]';

  return (
    <div className={cardWrapperCls}>
      <div className="p-4">
        {/* Agentic Phase 2 — explainable feed: agent badge + ranking reason + suggested action */}
        <FeedReasonBar
          reason={scoop._ranking?.reasons?.[0]}
          isAgent={scoop.generation_source === 'agent'}
          agentName={scoop.agent_name}
          action={scoop._ranking?.suggestedAction || null}
          onReply={() => { setShowReplies(true); setTimeout(() => replyInputRef.current?.focus(), 60); }}
          onReshare={() => setShowReshareConfirm(true)}
        />
        {scoop._ranking?.behavioral && <details className="mb-3 text-xs text-(--color-text-secondary)">
          <summary className="cursor-pointer">{t('feedDiagnostics.title', "Feed diagnostics · Superadmin")}</summary>
          <p className="mt-2">{scoop._ranking.reasons?.join(' · ') || t('feedDiagnostics.fresh', "Fresh content to explore")}</p>
          <p>{t('feedDiagnostics.relevance', { defaultValue: "Behavioral relevance: {multiplier}×. Source: {source}.", multiplier: scoop._ranking.behavioral.multiplier.toFixed(2), source: scoop._ranking.behavioral.source })}</p>
          <p>{t('feedDiagnostics.orderHint', "These are current factors; scrolling keeps the order from when you opened the feed.")}</p>
          <details className="mt-2"><summary className="cursor-pointer">{t('feedDiagnostics.rankingDetails', "Ranking details")}</summary><pre className="overflow-auto mt-2">{JSON.stringify(scoop._ranking,null,2)}</pre></details>
        </details>}
        {/* Product drop attribution */}
        {scoop.content_type === 'product_drop' && (
          <div className="flex items-center gap-1.5 text-[13px] text-[#9a9aac] mb-2 pl-1">
            <ShoppingBag size={12} className="text-amber-400" />
            <span>{getAuthorDisplayName(scoop)} dropped a product</span>
          </div>
        )}

        {/* Course launch attribution */}
        {scoop.content_type === 'course_launch' && (
          <div className="flex items-center gap-1.5 text-[13px] text-[#9a9aac] mb-2 pl-1">
            <GraduationCap size={12} className="text-iris-400" />
            <span>{getAuthorDisplayName(scoop)} launched a course</span>
          </div>
        )}

        {/* Site launch attribution */}
        {scoop.content_type === 'site_launch' && (
          <div className="flex items-center gap-1.5 text-[13px] text-[#9a9aac] mb-2 pl-1">
            <Globe size={12} className="text-emerald-400" />
            <span>{getAuthorDisplayName(scoop)} published a site</span>
          </div>
        )}

        {/* Reshare attribution */}
        {scoop.content_type === 'reshare' && scoop.reshared_scoop && (
          <div className="flex items-center gap-1.5 text-[13px] text-[#9a9aac] mb-2 pl-1">
            <Repeat2 size={12} className="text-green-400" />
            <span>{t('dashboardMain.card.reshared', { defaultValue: "{name} reshared", name: getAuthorDisplayName(scoop) })}</span>
          </div>
        )}

        {/* Author row */}
        <div className="flex items-center gap-3 mb-3">
          <Link aria-label={scoop.author_username ? `@${scoop.author_username}` : t('common.viewProfile', 'View profile')} to={scoop.author_username ? `/u/${scoop.author_username}` : `/u/${scoop.user_id}`} className="w-12 h-12 rounded-full bg-linear-to-br/srgb from-iris-500/40 to-iris-500/40 flex items-center justify-center text-lg text-white font-semibold overflow-hidden shrink-0 hover:ring-2 hover:ring-iris-500/50 transition-all">
            {scoop.author_avatar ? (
              <img src={scoop.author_avatar} alt="" className="w-full h-full object-cover" />
            ) : (
              getAuthorDisplayName(scoop).charAt(0).toUpperCase()
            )}
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Link to={scoop.author_username ? `/u/${scoop.author_username}` : `/u/${scoop.user_id}`} className="text-[15px] font-semibold text-white truncate hover:text-iris-400 transition-colors">{getAuthorDisplayName(scoop)}</Link>
              {scoop.author_is_verified && <VerifiedBadge className="w-4 h-4" />}
              {scoop.author_is_official && (
                <span className="inline-flex px-1.5 py-0.5 text-[9px] font-semibold bg-iris-500/15 text-iris-300 rounded-full uppercase tracking-wide">Channel</span>
              )}
              {authorBadges && authorBadges.length > 0 && <BadgeRow badges={authorBadges} />}
              {scoop.author_username && scoop.author_name && (
                <Link to={`/u/${scoop.author_username}`} className="text-[13px] text-[#8e8e9e] hover:text-iris-400 transition-colors">@{scoop.author_username}</Link>
              )}
              <span className="text-[13px] text-[#5e5e6e]">·</span>
              <span className="text-[13px] text-[#8e8e9e]">{formatTime(scoop.created_at)}</span>
              {scoop.edited_at && (
                <span className="text-[12px] text-[#4e4e60] italic" title={`${t('dashboardMain.card.edited', "edited")} ${formatDateTime(scoop.edited_at)}`}>{t('dashboardMain.card.edited', "edited")}</span>
              )}
              {scoop.certificate?.is_original && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[12px] font-medium">
                  <Award size={9} />
                  {t('dashboardMain.card.original', "Original")}
                </span>
              )}
              {scoop.certificate?.first_badge && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 text-[12px] font-medium">
                  <Sparkles size={9} />
                  {t('dashboardMain.card.first', "First")}
                </span>
              )}
            </div>
            {scoop.hub_id && scoop.hub_name && (
              <Link to={`/dashboard/scoops/${scoop.hub_id}`}
                className="text-xs text-iris-400 hover:text-iris-300 transition-colors"
              >
                {scoop.hub_icon || '📌'} {scoop.hub_name}
              </Link>
            )}
          </div>
          <div className="relative">
            <button
              aria-label={t('dashboardMain.feed.postOptions', "Post options")}
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 rounded-lg text-[#4e4e60] hover:text-white hover:bg-white/5 transition-colors"
            >
              <MoreHorizontal size={16} />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-8 z-50 w-40 rounded-xl bg-[#16161e] border border-white/10 shadow-xl overflow-hidden">
                  {onSave && <button onClick={()=>{setShowMenu(false);void onSave(scoop.id);}} className="w-full px-3 py-2 text-start text-xs hover:bg-white/5">{scoop.viewer_saved ? t('dashboardMain.feed.unsavePost', "Remove from saved") : t('dashboardMain.feed.savePost', "Save post")}</button>}
                  {onFeedFeedback && <>
                    <button onClick={()=>{setShowMenu(false);void onFeedFeedback(scoop.id,'not_interested');}} className="w-full px-3 py-2 text-start text-xs hover:bg-white/5">{t('dashboardMain.feed.notInterested', "Not interested")}</button>
                    <button onClick={()=>{setShowMenu(false);void onFeedFeedback(scoop.id,'hide');}} className="w-full px-3 py-2 text-start text-xs hover:bg-white/5">{t('dashboardMain.feed.hidePost', "Hide post")}</button>
                  </>}
                  <button
                    onClick={startEditing}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#b0b0c0] hover:bg-white/5 transition-colors"
                  >
                    <Pencil size={12} /> {t('dashboardMain.card.edit', "Edit")}
                  </button>
                  <button
                    onClick={() => { setShowDeleteConfirm(true); setShowMenu(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 size={12} /> {t('dashboardMain.card.delete', "Delete")}
                  </button>
                  {onReport && (
                    <button
                      onClick={openReportModal}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#b0b0c0] hover:bg-white/5 transition-colors border-t border-white/5"
                    >
                      <Flag size={12} /> {t('dashboardMain.card.report', "Report")}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content — edit mode or display */}
        {isEditing ? (
          <div className="mb-3 space-y-2">
            <div className="relative">
              <textarea aria-label={t('composer.ariaLabel', 'Write a post')}
                ref={editRef}
                value={editText}
                onChange={(e) => {
                  setEditText(e.target.value);
                  detectMention(e.target.value, editRef.current, setEditMentionQuery, setEditMentionPos);
                }}
                onKeyDown={(e) => {
                  if (editMentionQuery !== null) return;
                  if (e.key === 'Escape') cancelEditing();
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveEdit(); }
                }}
                className="w-full bg-white/4 border border-iris-500/30 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#4e4e60] focus:outline-hidden focus:border-iris-500/50 resize-none min-h-[60px] transition-colors"
                rows={3}
              />
              {editMentionQuery !== null && (
                <MentionDropdown
                  query={editMentionQuery}
                  position={editMentionPos}
                  onSelect={(user) => {
                    if (!editRef.current) return;
                    const cursor = editRef.current.selectionStart;
                    const before = editText.slice(0, cursor);
                    const after = editText.slice(cursor);
                    const atIndex = before.lastIndexOf('@');
                    const newText = before.slice(0, atIndex) + `@${user.displayName} ` + after;
                    setEditText(newText);
                    setEditMentionQuery(null);
                    setTimeout(() => {
                      const newCursor = atIndex + user.displayName.length + 2;
                      editRef.current?.focus();
                      editRef.current?.setSelectionRange(newCursor, newCursor);
                    }, 0);
                  }}
                  onClose={() => setEditMentionQuery(null)}
                />
              )}
            </div>
            {/* Edit tags */}
            <div className="flex flex-wrap items-center gap-1.5">
              {editTags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-iris-500/15 text-iris-300 text-xs">
                  #{tag}
                  <button onClick={() => setEditTags(prev => prev.filter(t => t !== tag))} className="text-iris-400/50 hover:text-iris-300">
                    <X size={10} />
                  </button>
                </span>
              ))}
              <input
                value={editTagInput}
                onChange={(e) => setEditTagInput(e.target.value.replace(/\s/g, ''))}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ',' || e.key === ' ') && editTagInput.trim()) { e.preventDefault(); addEditTag(editTagInput); }
                  if (e.key === 'Backspace' && !editTagInput && editTags.length > 0) setEditTags(prev => prev.slice(0, -1));
                }}
                onBlur={() => { if (editTagInput.trim()) addEditTag(editTagInput); }}
                placeholder={t('dashboardMain.card.addTag', "Add tag...")}
                className="flex-1 min-w-[60px] bg-transparent text-xs text-white placeholder:text-[#4e4e60] focus:outline-hidden"
              />
            </div>
            {/* Edit media — existing + new previews */}
            {(editMediaUrls.length > 0 || editNewImagePreviews.length > 0) && (() => {
              const allPreviews = [...editMediaUrls, ...editNewImagePreviews];
              const totalCount = allPreviews.length;
              return (
                <div>
                  <div className={`grid gap-1 rounded-xl overflow-hidden ${
                    totalCount === 1 ? 'grid-cols-1' :
                    totalCount === 2 ? 'grid-cols-2' :
                    'grid-cols-2 grid-rows-2'
                  }`}>
                    {allPreviews.map((url, i) => {
                      const isExisting = i < editMediaUrls.length;
                      return (
                        <div
                          key={i}
                          className={`relative group/img ${
                            totalCount === 3 && i === 0 ? 'row-span-2' : ''
                          }`}
                        >
                          {(url.startsWith('data:video/') || /\.(webm|mp4|mov|ogg)(\?|$)/i.test(url)) ? (
                            <video
                              src={url}
                              controls
                              className={`w-full border border-white/6 ${
                                totalCount === 1 ? 'max-h-48 rounded-xl' : 'h-[120px]'
                              } ${totalCount === 3 && i === 0 ? 'h-full!' : ''}`}
                            />
                          ) : (
                            <img
                              src={url}
                              alt=""
                              className={`w-full object-cover border border-white/6 ${
                                totalCount === 1 ? 'max-h-48 rounded-xl' : 'h-[120px]'
                              } ${totalCount === 3 && i === 0 ? 'h-full!' : ''}`}
                            />
                          )}
                          <button
                            onClick={() => {
                              if (isExisting) {
                                setEditMediaUrls(prev => prev.filter((_, idx) => idx !== i));
                              } else {
                                const newIdx = i - editMediaUrls.length;
                                setEditNewImages(prev => prev.filter((_, idx) => idx !== newIdx));
                                setEditNewImagePreviews(prev => prev.filter((_, idx) => idx !== newIdx));
                              }
                            }}
                            className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/70 text-white hover:bg-red-500/80 transition-colors opacity-0 group-hover/img:opacity-100"
                            title={t('dashboardMain.card.removeImage', "Remove image")}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-1 text-[12px] text-[#9a9aac]">{t('composer.imagesCount', { defaultValue: "{count}/4 images", count: totalCount })}</div>
                </div>
              );
            })()}
            {/* Edit toolbar */}
            <input
              ref={editImageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                const totalExisting = editMediaUrls.length + editNewImages.length;
                const remaining = 4 - totalExisting;
                if (remaining <= 0) return;
                const toAdd = files.slice(0, remaining);
                setEditNewImages(prev => [...prev, ...toAdd]);
                toAdd.forEach(file => {
                  const reader = new FileReader();
                  reader.onload = () => setEditNewImagePreviews(prev => [...prev, reader.result as string]);
                  reader.readAsDataURL(file);
                });
                if (editImageInputRef.current) editImageInputRef.current.value = '';
              }}
              className="hidden"
            />
            <div className="flex items-center justify-between pt-1 border-t border-white/4">
              <div className="flex items-center gap-0.5">
                <button
                  onClick={() => (editMediaUrls.length + editNewImages.length) < 4 ? editImageInputRef.current?.click() : undefined}
                  className={`p-1.5 rounded-lg text-xs transition-colors ${
                    (editMediaUrls.length + editNewImages.length) > 0 ? 'bg-emerald-500/15 text-emerald-400' : 'text-[#9a9aac] hover:text-white hover:bg-white/5'
                  } ${(editMediaUrls.length + editNewImages.length) >= 4 ? 'opacity-40 cursor-not-allowed' : ''}`}
                  title={(editMediaUrls.length + editNewImages.length) >= 4 ? t('composer.maxImages', "Max 4 images") : t('composer.addImage', "Add image")}
                >
                  <ImagePlus size={14} />
                </button>
                <button
                  onClick={() => {
                    if (editRef.current) {
                      const pos = editRef.current.selectionStart;
                      const before = editText.slice(0, pos);
                      const after = editText.slice(pos);
                      const newText = before + '@' + after;
                      setEditText(newText);
                      setTimeout(() => {
                        editRef.current?.focus();
                        editRef.current?.setSelectionRange(pos + 1, pos + 1);
                        detectMention(newText, editRef.current, setEditMentionQuery, setEditMentionPos);
                      }, 0);
                    }
                  }}
                  className={`p-1.5 rounded-lg text-xs transition-colors ${editMentionQuery !== null ? 'bg-blue-500/15 text-blue-400' : 'text-[#9a9aac] hover:text-white hover:bg-white/5'}`}
                  title={t('composer.mentionSomeone', "Mention someone")}
                >
                  <AtSign size={14} />
                </button>
                <span className="text-[12px] text-[#4e4e60] ml-1">{t('dashboardMain.card.escCancelCmdSave', "Esc cancel, Cmd+Enter save")}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={cancelEditing} className="px-3 py-1 rounded-lg text-xs text-[#9a9aac] hover:text-white hover:bg-white/5 transition-colors">
                  {t('dashboardMain.card.cancel', "Cancel")}
                </button>
                <button
                  onClick={saveEdit}
                  disabled={saving || editUploading}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium text-[#0C0C12] bg-iris-600 hover:bg-iris-500 disabled:opacity-40 transition-colors"
                >
                  {(saving || editUploading) ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                  {editUploading ? t('composer.uploading', "Uploading...") : t('dashboardMain.card.save', "Save")}
                </button>
              </div>
            </div>
          </div>
        ) : scoop.description ? (
          <Link to={`?scoop=${scoop.id}`} preventScrollReset className="block mb-3">
            <p
              {...rtlContentProps(scoop.description)}
              className="text-[15px] text-[#e0e0f0] leading-relaxed whitespace-pre-wrap hover:text-white transition-colors"
            >{renderTextWithMentions(scoop.description, scoop.tracked_links)}</p>
          </Link>
        ) : null}

        {/* Poll card */}
        {!isEditing && scoop.content_type === 'poll' && <PollCard scoopId={scoop.id} />}

        {/* Event card */}
        {!isEditing && scoop.content_type === 'event' && <EventCard scoopId={scoop.id} />}

        {/* Product drop card */}
        {!isEditing && scoop.content_type === 'product_drop' && scoop.metadata?.product_id && (
          <Link
            to={`/shop/${scoop.metadata.shop_handle}/${scoop.metadata.product_slug}`}
            className="block rounded-xl border border-white/5 bg-white/2 hover:bg-white/4 transition-colors overflow-hidden mb-3"
          >
            {scoop.image_url && (
              <div className="h-48 overflow-hidden">
                <img src={scoop.image_url} alt={scoop.title || ''} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                {scoop.title && <h4 className="text-sm font-medium text-white mb-0.5 truncate">{decodeEntities(scoop.title)}</h4>}
                {scoop.metadata.shop_name && (
                  <p className="text-xs text-[#9a9aac] truncate">{scoop.metadata.shop_name}</p>
                )}
              </div>
              <span className="text-sm font-semibold text-amber-400 whitespace-nowrap">
                {fmtShopPrice(scoop.metadata.product_price, scoop.metadata.product_currency)}
              </span>
            </div>
          </Link>
        )}

        {/* Course launch card */}
        {!isEditing && scoop.content_type === 'course_launch' && scoop.metadata?.course_id && (
          <Link
            to={`/courses/${scoop.metadata.slug || scoop.metadata.course_id}`}
            className="block rounded-xl border border-white/5 bg-white/2 hover:bg-white/4 transition-colors overflow-hidden mb-3"
          >
            {scoop.image_url && (
              <div className="h-48 overflow-hidden">
                <img src={scoop.image_url} alt={scoop.title || ''} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-3">
              {scoop.title && <h4 className="text-sm font-medium text-white mb-1 truncate">{decodeEntities(scoop.title)}</h4>}
              <div className="flex items-center gap-3 text-xs text-[#9a9aac]">
                {scoop.metadata.difficulty && <span className="capitalize">{scoop.metadata.difficulty}</span>}
                <span>{scoop.metadata.price > 0 ? fmtShopPrice(scoop.metadata.price, scoop.metadata.currency) : 'Free'}</span>
              </div>
            </div>
          </Link>
        )}

        {/* Site launch card */}
        {!isEditing && scoop.content_type === 'site_launch' && scoop.metadata?.site_id && (
          <Link
            to={`/s/${scoop.metadata.handle || scoop.metadata.site_id}`}
            className="block rounded-xl border border-white/5 bg-white/2 hover:bg-white/4 transition-colors overflow-hidden mb-3 p-3"
          >
            <div className="flex items-center gap-2 mb-1">
              <Globe size={14} className="text-iris-400" />
              {scoop.title && <h4 className="text-sm font-medium text-white truncate">{decodeEntities(scoop.title)}</h4>}
            </div>
            {scoop.description && <p className="text-xs text-[#9a9aac] line-clamp-2">{scoop.description}</p>}
          </Link>
        )}

        {/* Scheduled badge */}
        {scoop.status === 'scheduled' && scoop.scheduled_at && (
          <div className="flex items-center gap-1.5 mb-3 px-2 py-1 rounded-lg bg-orange-500/6 border border-orange-500/10 w-fit">
            <Clock size={12} className="text-orange-400" />
            <span className="text-[12px] text-orange-400 font-medium">
              {t('dashboardMain.card.scheduledFor', { defaultValue: "Scheduled for {date}", date: formatDateTime(scoop.scheduled_at) })}
            </span>
          </div>
        )}

        {/* Gated content overlay */}
        {scoop.is_gated && !scoop.viewer_has_access && !isEditing && (
          <div className="relative mb-3 rounded-xl overflow-hidden">
            {scoop.description && (
              <div className="blur-xs pointer-events-none select-none opacity-50">
                <p className="text-sm text-[#d0d0e0] leading-relaxed">{scoop.description.substring(0, 80)}...</p>
              </div>
            )}
            <div className="flex flex-col items-center justify-center py-6 bg-linear-to-b/srgb from-transparent via-[#0a0a0f]/80 to-[#0a0a0f] rounded-xl">
              <Lock size={18} className="text-amber-400 mb-2" />
              <p className="text-xs text-[#b0b0c0] mb-2">{t('dashboardMain.card.subscribersOnly', "Subscribers only")}</p>
              {scoop.hub_id && (
                <Link to={`/dashboard/scoops/${scoop.hub_id}`}
                  className="px-4 py-1.5 rounded-lg text-xs font-medium text-[#0C0C12] bg-iris-600 hover:bg-iris-500 transition-colors">
                  {t('dashboardMain.card.subscribeToUnlock', "Subscribe to unlock")}
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Media carousel (uploaded images) */}
        {!isEditing && !(scoop.is_gated && !scoop.viewer_has_access) && scoop.media_urls && scoop.media_urls.length > 0 && (
          <div className="mb-3">
            <ImageCarousel images={scoop.media_urls} mediaTypes={scoop.metadata?.media_types} defaultType={scoop.content_type} aiEdited={isAiEditedPost(scoop as any)} reportTarget={{ type: 'scoop', id: scoop.id }} />
          </div>
        )}

        {/* Video embed */}
        {!isEditing && scoop.content_type === 'video' && scoop.metadata?.embedUrl && (
          <div className="mb-3 rounded-xl overflow-hidden border border-white/5">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={scoop.metadata.embedUrl}
                className="absolute inset-0 w-full h-full"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Video"
              />
            </div>
            {scoop.title && (
              <div className="px-3 py-2 flex items-center gap-2">
                <span className="text-[12px] uppercase font-medium tracking-wider text-iris-400">{scoop.metadata.videoPlatform}</span>
                <span className="text-xs text-white truncate">{decodeEntities(scoop.title)}</span>
              </div>
            )}
          </div>
        )}

        {/* Link preview */}
        {isEditing ? (
          !editUrlRemoved && editUrl ? (
            <div className="relative rounded-xl border border-white/5 bg-white/2 overflow-hidden mb-3 group/link">
              <button
                onClick={() => setEditUrlRemoved(true)}
                className="absolute top-2 right-2 z-10 p-1 rounded-full bg-black/60 text-white/60 hover:text-white hover:bg-red-500/80 transition-colors"
                title={t('dashboardMain.card.removeLink', "Remove link")}
              >
                <X size={14} />
              </button>
              {scoop.image_url && (
                <div className="h-40 overflow-hidden">
                  <img src={scoop.image_url} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-3">
                <input
                  type="url"
                  value={editUrl}
                  onChange={e => setEditUrl(e.target.value)}
                  className="w-full bg-transparent text-sm text-white outline-hidden border-b border-white/10 pb-1 mb-1 focus:border-iris-500/50 transition-colors"
                  placeholder="URL"
                />
                <div className="flex items-center gap-1 text-xs text-[#9a9aac]">
                  <ExternalLink size={10} />
                  <span className="truncate">{(() => { try { return new URL(editUrl).hostname; } catch { return editUrl; } })()}</span>
                </div>
              </div>
            </div>
          ) : editUrlRemoved ? (
            <button
              onClick={() => { setEditUrlRemoved(false); setEditUrl(scoop.url || ''); }}
              className="flex items-center gap-1.5 text-xs text-[#9a9aac] hover:text-iris-400 mb-3 transition-colors"
            >
              <Link2 size={12} />
              {t('dashboardMain.card.restoreLink', "Restore link")}
            </button>
          ) : null
        ) : scoop.content_type !== 'video' && scoop.url ? (
          <a
            href={scoop.tracked_url || trackedUrl(scoop.url)}
            target="_blank"
            rel="noopener noreferrer"
            // V2 chromeless feed needs the link card to actually read against
            // the page background — the old `bg-white/2` + `border-white/5`
            // disappeared into `#0a0a0f`. We bump both, and add the external-
            // link icon next to the title so the affordance is unmissable.
            className={scoopDesignV2
              ? 'block rounded-xl border border-white/10 bg-white/4 hover:bg-white/[0.07] hover:border-iris-400/30 transition-colors overflow-hidden mb-3 group'
              : 'block rounded-xl border border-white/5 bg-white/2 hover:bg-white/4 transition-colors overflow-hidden mb-3'}
          >
            {scoop.image_url && (
              <div className="h-40 overflow-hidden">
                <img src={scoop.image_url} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-3">
              {scoop.title && (
                <h4 className={scoopDesignV2
                  ? 'text-sm font-semibold text-white mb-1 line-clamp-2 group-hover:text-iris-300 transition-colors'
                  : 'text-sm font-medium text-white mb-1 line-clamp-2'}>
                  {decodeEntities(scoop.title)}
                </h4>
              )}
              <div className="flex items-center gap-1 text-xs text-[#9a9aac]">
                <ExternalLink size={10} />
                <span className="truncate">{new URL(scoop.url).hostname}</span>
              </div>
            </div>
          </a>
        ) : null}

        {/* AI Summary for link posts */}
        {scoop.url && scoop.content_type !== 'video' && !isEditing && (
          <div className="mt-2">
            {!urlSummary && !urlSummaryLoading && (
              <button
                onClick={handleSummarizeUrl}
                className="flex items-center gap-1.5 text-[13px] text-[#9a9aac] hover:text-iris-400 transition-colors"
              >
                <Sparkles size={14} />
                {t('hubScoopCard.summarize', "Summarize article")}
              </button>
            )}
            {urlSummaryLoading && (
              <div className="flex items-center gap-2 text-[13px] text-[#9a9aac]">
                <Loader2 size={14} className="animate-spin" />
                {t('hubScoopCard.summarizing', "Summarizing...")}
              </div>
            )}
            {urlSummaryError && (
              <p className="text-[13px] text-red-400/80">{urlSummaryError}</p>
            )}
            {urlSummary && (
              <div className="mt-2 p-3 rounded-xl bg-iris-500/6 border border-iris-500/10">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Sparkles size={12} className="text-iris-400" />
                  <span className="text-[11px] font-medium text-iris-400 uppercase tracking-wider">
                    {t('hubScoopCard.aiSummary', "AI Summary")}
                  </span>
                  <button
                    onClick={() => setUrlSummary(null)}
                    className="ml-auto text-[#9a9aac] hover:text-white transition-colors"
                  >
                    <X size={12} />
                  </button>
                </div>
                <p dir="auto" className="text-[13px] text-[#b0b0c0] leading-relaxed whitespace-pre-wrap">
                  {urlSummary}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Tags */}
        {scoop.tags && scoop.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {scoop.tags.map(tag => (
              <span
                key={tag}
                className="px-2 py-0.5 rounded-md bg-iris-500/10 text-iris-300 text-xs"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Social proof */}
        {scoop.like_count > 0 && (
          <div className="mb-2">
            <SocialProofBadge entityType="scoop" entityId={scoop.id} action="scoop_liked" />
          </div>
        )}

        {/* Post ROI — visible only to author */}
        <PostROIBadge scoopId={scoop.id} isOwner={scoop.user_id === localStorage.getItem('userId')} />

        {/* Engagement bar */}
        <div className="relative flex items-center gap-1 pt-3 border-t border-white/5">
          {/* Scoop vote button */}
          <button
            onClick={() => onScoopVote(scoop.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              scoop.viewer_voted ? 'text-iris-400 bg-iris-500/15' : 'text-[#8e8e9e] hover:text-iris-400 hover:bg-iris-500/5'
            }`}
            title={t('dashboardMain.card.upvoteTitle', "Post — upvote quality content")}
          >
            <Sparkles size={18} fill={scoop.viewer_voted ? 'currentColor' : 'none'} />
            <span>{scoop.scoop_count || 0}</span>
          </button>
          {/* Heart/Like button */}
          <button
            onClick={() => onLike(scoop.id)}
            title={scoop.viewer_liked ? t('dashboardMain.card.unlike', 'Unlike') : t('dashboardMain.card.like', 'Like')}
            aria-label={scoop.viewer_liked ? t('dashboardMain.card.unlike', 'Unlike') : t('dashboardMain.card.like', 'Like')}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
              scoop.viewer_liked ? 'text-red-400 bg-red-500/10' : 'text-[#8e8e9e] hover:text-red-400 hover:bg-red-500/5'
            }`}
          >
            <Heart size={18} fill={scoop.viewer_liked ? 'currentColor' : 'none'} />
            {scoop.like_count > 0 && <span>{scoop.like_count}</span>}
          </button>
          {/* Reply button */}
          <button
            onClick={toggleReplies}
            title={t('dashboardMain.card.reply', 'Reply')}
            aria-label={t('dashboardMain.card.reply', 'Reply')}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
              showReplies ? 'text-iris-400 bg-iris-500/10' : 'text-[#8e8e9e] hover:text-iris-400 hover:bg-iris-500/5'
            }`}
          >
            <MessageSquare size={18} />
            {scoop.reply_count > 0 && <span>{scoop.reply_count}</span>}
          </button>
          {/* Reshare button */}
          <button
            onClick={handleReshareClick}
            title={t('dashboardMain.card.reshare', 'Reshare or quote')}
            aria-label={t('dashboardMain.card.reshare', 'Reshare')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-[#8e8e9e] hover:text-green-400 hover:bg-green-500/5 transition-colors"
          >
            <Repeat2 size={18} />
            {scoop.reshare_count > 0 && <span>{scoop.reshare_count}</span>}
          </button>
          {/* Tip button */}
          <div className="relative">
            <button
              onClick={() => setShowTipModal(!showTipModal)}
              className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm text-[#8e8e9e] hover:text-emerald-400 hover:bg-emerald-500/5 transition-colors"
              title={t('dashboardMain.card.tipCreator', "Tip creator")}
            >
              <DollarSign size={18} />
            </button>
            {showTipModal && (
              <div className="absolute bottom-full left-0 mb-1 p-3 bg-[#16161e] border border-white/10 rounded-xl shadow-xl z-50 w-[220px]">
                <p className="text-[12px] text-[#9a9aac] uppercase tracking-wider mb-2">{t('dashboardMain.tip.sendATip', "Send a tip")}</p>
                {/* Credit balance */}
                <div className="flex items-center justify-between mb-2 px-1 flex-wrap gap-y-2">
                  <span className="text-[12px] text-[#9a9aac]">{t('dashboardMain.tip.yourBalance', "Your balance")}</span>
                  <span className="text-xs font-medium text-emerald-400">
                    {creditBalance !== null ? `$${(creditBalance / 100).toFixed(2)}` : '...'}
                  </span>
                </div>
                {creditBalance !== null && creditBalance < 100 ? (
                  <div className="text-center py-2">
                    <p className="text-[13px] text-[#8e8ea0] mb-2">{t('dashboardMain.tip.needCredits', "You need credits to tip")}</p>
                    <a
                      href="/dashboard/credits"
                      className="inline-block px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-500 transition-colors"
                    >
                      {t('dashboardMain.tip.buyCredits', "Buy Credits")}
                    </a>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-1.5 mb-2">
                      {[100, 300, 500].map(amt => (
                        <button
                          key={amt}
                          onClick={() => setTipAmount(amt)}
                          className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            tipAmount === amt ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-white/4 text-[#b0b0c0] border border-white/6 hover:border-white/10'
                          }`}
                        >
                          ${amt / 100}
                        </button>
                      ))}
                    </div>
                    <input
                      type="number"
                      value={tipAmount / 100}
                      onChange={e => setTipAmount(Math.round(parseFloat(e.target.value || '0') * 100))}
                      min={1} max={500} step={1}
                      className="w-full px-2.5 py-1.5 bg-white/4 border border-white/6 rounded-lg text-xs text-white outline-hidden focus:border-emerald-500/50 mb-2"
                      placeholder={t('dashboardMain.tip.customAmount', "Custom amount")}
                    />
                    <input
                      type="text"
                      value={tipMessage}
                      onChange={e => setTipMessage(e.target.value.slice(0, 200))}
                      className="w-full px-2.5 py-1.5 bg-white/4 border border-white/6 rounded-lg text-xs text-white outline-hidden focus:border-emerald-500/50 mb-2"
                      placeholder={t('dashboardMain.tip.optionalMessage', "Optional message...")}
                    />
                    {creditBalance !== null && tipAmount > creditBalance && (
                      <p className="text-[12px] text-red-400 mb-1">{t('dashboardMain.tip.insufficientCredits', "Insufficient credits.")} <a href="/dashboard/credits" className="underline">{t('dashboardMain.tip.buyMore', "Buy more")}</a></p>
                    )}
                    <button
                      onClick={async () => {
                        if (tipAmount < 100) return;
                        setTipping(true);
                        try {
                          await onTip(scoop.id, tipAmount, tipMessage || undefined);
                          setCreditBalance(prev => prev !== null ? prev - tipAmount : null);
                        } catch {}
                        setTipping(false);
                        setShowTipModal(false);
                        setTipMessage('');
                        setTipAmount(300);
                      }}
                      disabled={tipping || tipAmount < 100 || (creditBalance !== null && tipAmount > creditBalance)}
                      className="w-full py-1.5 rounded-lg text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 transition-colors"
                    >
                      {tipping ? t('dashboardMain.tip.sending', "Sending...") : t('dashboardMain.tip.tipAmount', { defaultValue: "Tip ${amount}", amount: (tipAmount / 100).toFixed(2) })}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          {/* Fact check button */}
          {(scoop.description || scoop.title) && (
            <button
              onClick={handleFactCheck}
              className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                showFactCheck ? 'text-cyan-400 bg-cyan-500/10' : 'text-[#8e8e9e] hover:text-cyan-400 hover:bg-cyan-500/5'
              }`}
              title={t('dashboardMain.factCheckPanel.title', "Fact Check")}
            >
              <ShieldCheck size={18} />
              <span className="text-xs hidden sm:inline">{t('dashboardMain.card.factCheck', "Fact Check")}</span>
            </button>
          )}
          {/* Reaction picker toggle */}
          <div>
            <button
              onClick={onToggleReactionPicker}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs text-[#9a9aac] hover:text-white hover:bg-white/5 transition-colors"
              title={t('dashboardMain.card.addReaction', "Add reaction")}
            >
              <SmilePlus size={14} />
            </button>
            {reactionPickerOpen && (
              <div className="absolute bottom-full left-0 mb-1 p-2.5 bg-[#16161e] border border-white/10 rounded-xl shadow-xl z-50 w-[280px]">
                {/* Quick picks - top row */}
                <div className="flex gap-0.5 mb-2 pb-2 border-b border-white/5">
                  {['👍', '❤️', '🔥', '😂', '😮', '😢', '👎'].map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => { onReaction(scoop.id, emoji); onToggleReactionPicker(); }}
                      className="flex-1 h-9 flex items-center justify-center rounded-lg hover:bg-white/10 hover:scale-125 transition-all text-lg"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                {/* Feelings */}
                <p className="text-[13px] text-[#4e4e60] uppercase tracking-wider mb-1 px-0.5">{t('dashboardMain.reactions.feelings', "Feelings")}</p>
                <div className="grid grid-cols-8 gap-0.5 mb-2">
                  {['😍', '🤩', '🥳', '🤔', '😤', '💀', '🙄', '😈', '😭', '🥺', '😱', '🤡', '😴', '🤮', '😏', '🫡'].map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => { onReaction(scoop.id, emoji); onToggleReactionPicker(); }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 hover:scale-110 transition-all text-base"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                {/* Vibes */}
                <p className="text-[13px] text-[#4e4e60] uppercase tracking-wider mb-1 px-0.5">{t('dashboardMain.reactions.vibes', "Vibes")}</p>
                <div className="grid grid-cols-8 gap-0.5">
                  {['💯', '🚀', '💡', '🎯', '⚡', '✨', '👏', '🙌', '💪', '🏆', '🤯', '👀', '🫶', '💎', '🎉', '🤝'].map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => { onReaction(scoop.id, emoji); onToggleReactionPicker(); }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 hover:scale-110 transition-all text-base"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Share button */}
          <div className="relative">
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm text-[#8e8e9e] hover:text-white hover:bg-white/5 transition-colors"
              title={t('dashboardMain.card.share', "Share")}
            >
              <Share2 size={18} />
            </button>
            {showShareMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowShareMenu(false)} />
                <div className="absolute bottom-full right-0 mb-1 w-48 rounded-xl bg-[#16161e] border border-white/10 shadow-xl z-50 overflow-hidden">
                  <button
                    onClick={handleCopyLink}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#b0b0c0] hover:bg-white/5 transition-colors"
                  >
                    <Copy size={12} />
                    {linkCopied ? t('dashboardMain.card.copied', "Copied!") : t('dashboardMain.card.copyLink', "Copy Link")}
                  </button>
                  <button
                    onClick={handleShareX}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#b0b0c0] hover:bg-white/5 transition-colors"
                  >
                    <ExternalLink size={12} />
                    {t('dashboardMain.card.shareToX', "Share to X")}
                  </button>
                  <button
                    onClick={handleShareLinkedIn}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#b0b0c0] hover:bg-white/5 transition-colors"
                  >
                    <ExternalLink size={12} />
                    {t('dashboardMain.card.shareToLinkedIn', "Share to LinkedIn")}
                  </button>
                </div>
              </>
            )}
          </div>
          {scoop.is_gated && (
            <span className="flex items-center gap-1 text-[12px] text-amber-400/80 ml-auto">
              <Lock size={10} /> {t('dashboardMain.card.subscribers', "Subscribers")}
            </span>
          )}
          {scoop.is_pinned && (
            <span className="flex items-center gap-1 text-xs text-amber-400 ml-auto">
              <Pin size={12} /> {t('dashboardMain.card.pinned', "Pinned")}
            </span>
          )}
        </div>
        {/* Emoji reactions display */}
        {reactions.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 pt-1.5">
            {reactions.map((r) => (
              <button
                key={r.emoji}
                onClick={() => onReaction(scoop.id, r.emoji)}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-xs transition-colors ${
                  r.viewerReacted
                    ? 'bg-iris-500/15 text-iris-300 border border-iris-500/30'
                    : 'bg-white/3 text-[#a0a0b0] border border-white/5 hover:bg-white/6 hover:border-white/10'
                }`}
              >
                <span className="text-sm">{r.emoji}</span>
                <span>{r.count}</span>
              </button>
            ))}
          </div>
        )}
        {/* Outcome reactions */}
        <OutcomeReactions scoopId={scoop.id} isLoggedIn={!!getAuthToken()} />
      </div>

      {/* Fact-check panel */}
      {showFactCheck && (
        <div className="px-4 pb-3">
          <div className="rounded-xl bg-cyan-500/3 border border-cyan-500/10 overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-cyan-500/10">
              <ShieldCheck size={14} className="text-cyan-400" />
              <span className="text-xs font-semibold text-cyan-300 uppercase tracking-wider">{t('dashboardMain.factCheckPanel.title', "Fact Check")}</span>
              <span className="text-[10px] text-[#9a9aac] ml-auto">{t('dashboardMain.factCheckPanel.poweredByAI', "Powered by AI")}</span>
            </div>
            {factCheckLoading ? (
              <div className="flex items-center gap-2 px-3 py-4 justify-center">
                <Loader2 size={14} className="animate-spin text-cyan-400" />
                <span className="text-xs text-[#8e8ea0]">{t('dashboardMain.factCheckPanel.analyzingClaims', "Analyzing claims...")}</span>
              </div>
            ) : factCheckData ? (
              <div className="p-3 space-y-2.5">
                {/* Overall badge */}
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider ${
                    factCheckData.overall === 'accurate' ? 'bg-green-500/15 text-green-400' :
                    factCheckData.overall === 'mostly_accurate' ? 'bg-emerald-500/15 text-emerald-400' :
                    factCheckData.overall === 'mixed' ? 'bg-yellow-500/15 text-yellow-400' :
                    factCheckData.overall === 'misleading' ? 'bg-red-500/15 text-red-400' :
                    'bg-white/6 text-[#8e8ea0]'
                  }`}>
                    {factCheckData.overall === 'accurate' ? t('dashboardMain.factCheckPanel.accurate', "Accurate") :
                     factCheckData.overall === 'mostly_accurate' ? t('dashboardMain.factCheckPanel.mostlyAccurate', "Mostly Accurate") :
                     factCheckData.overall === 'mixed' ? t('dashboardMain.factCheckPanel.mixed', "Mixed") :
                     factCheckData.overall === 'misleading' ? t('dashboardMain.factCheckPanel.misleading', "Misleading") :
                     t('dashboardMain.factCheckPanel.noClaimsFound', "No claims found")}
                  </span>
                </div>
                {/* Summary */}
                <p className="text-[13px] text-[#b0b0c0] leading-relaxed">{factCheckData.summary}</p>
                {/* Individual claims */}
                {factCheckData.claims.length > 0 && (
                  <div className="space-y-2 pt-1">
                    {factCheckData.claims.map((c, i) => (
                      <div key={i} className="rounded-lg bg-white/2 border border-white/4 p-2.5">
                        <div className="flex items-start gap-2">
                          <span className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${
                            c.verdict === 'true' ? 'bg-green-400' :
                            c.verdict === 'mostly_true' ? 'bg-emerald-400' :
                            c.verdict === 'unverified' ? 'bg-yellow-400' :
                            c.verdict === 'misleading' ? 'bg-orange-400' :
                            'bg-red-400'
                          }`} />
                          <div className="min-w-0">
                            <p className="text-[13px] text-[#d0d0e0] leading-snug">{c.claim}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-[11px] font-semibold uppercase tracking-wider ${
                                c.verdict === 'true' ? 'text-green-400' :
                                c.verdict === 'mostly_true' ? 'text-emerald-400' :
                                c.verdict === 'unverified' ? 'text-yellow-400' :
                                c.verdict === 'misleading' ? 'text-orange-400' :
                                'text-red-400'
                              }`}>
                                {c.verdict === 'true' ? t('dashboardMain.factCheckPanel.verdictTrue', "True") :
                                 c.verdict === 'mostly_true' ? t('dashboardMain.factCheckPanel.verdictMostlyTrue', "Mostly True") :
                                 c.verdict === 'unverified' ? t('dashboardMain.factCheckPanel.verdictUnverified', "Unverified") :
                                 c.verdict === 'misleading' ? t('dashboardMain.factCheckPanel.verdictMisleading', "Misleading") :
                                 t('dashboardMain.factCheckPanel.verdictFalse', "False")}
                              </span>
                              <span className="text-[11px] text-[#9a9aac]">{c.explanation}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-[#828294] pt-1">{t('dashboardMain.factCheckPanel.disclaimer', "AI-generated analysis. May contain errors. Verify important claims independently.")}</p>
              </div>
            ) : (
              <div className="px-3 py-4 text-center">
                <p className="text-xs text-[#9a9aac]">{t('dashboardMain.factCheckPanel.unableToAnalyze', "Unable to analyze this post.")}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reshare confirmation popover */}
      {showReshareConfirm && (
        // Reshare composer — optional comment turns this into a quote
        // post (the API already supports a `comment` body field).
        // Empty comment + Reshare = plain repost; with text = quote
        // post with the original embedded.
        <div className="px-4 pb-3">
          <div className="rounded-xl bg-green-500/4 border border-green-500/20 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Repeat2 size={14} className="text-green-400" />
              <span className="text-xs text-[#a0a0b0]">
                {t('dashboardMain.reshare.addCommentHint', 'Add your take, or repost as-is')}
              </span>
            </div>
            <textarea
              value={reshareComment}
              onChange={(e) => setReshareComment(e.target.value)}
              placeholder={t('dashboardMain.reshare.placeholder', 'What do you think? (optional)')}
              rows={2}
              {...rtlContentProps(reshareComment)}
              className="w-full bg-transparent text-sm text-white placeholder:text-[#4e4e60] resize-none focus:outline-hidden mb-2"
              autoFocus
            />
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowReshareConfirm(false); setReshareComment(''); }}
                className="px-3 py-1.5 rounded-lg text-xs text-[#9a9aac] hover:text-white hover:bg-white/5 transition-colors"
              >
                {t('dashboardMain.reshare.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => confirmReshare(reshareComment)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-green-600 hover:bg-green-500 transition-colors"
              >
                {reshareComment.trim()
                  ? t('dashboardMain.reshare.quote', 'Quote post')
                  : t('dashboardMain.reshare.reshare', 'Reshare')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-70 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-xs" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative w-full max-w-sm bg-[#16161e] border border-white/8 rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
                  <Trash2 size={18} className="text-red-400" />
                </div>
                <div>
                  <h3 className="text-[15px] font-semibold text-white">{t('dashboardMain.deleteConfirm.title', "Delete post?")}</h3>
                  <p className="text-xs text-[#9a9aac] mt-0.5">{t('dashboardMain.deleteConfirm.description', "This can't be undone. The post will be permanently removed.")}</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 mt-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 rounded-xl text-sm text-[#b0b0c0] hover:text-white hover:bg-white/5 transition-colors"
                >
                  {t('dashboardMain.deleteConfirm.cancel', "Cancel")}
                </button>
                <button
                  onClick={() => { onDelete(scoop.id); setShowDeleteConfirm(false); }}
                  className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-500 transition-colors"
                >
                  {t('dashboardMain.deleteConfirm.delete', "Delete")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inline replies section */}
      {showReplies && (
        <div className="border-t border-white/5 bg-white/1">
          {/* Reply composer */}
          <div className="p-3 flex gap-2">
            <div className="w-7 h-7 rounded-full bg-linear-to-br/srgb from-iris-500/20 to-iris-500/20 flex items-center justify-center text-[12px] text-white font-medium shrink-0 mt-0.5">
              {t('dashboardMain.card.you', "You")}
            </div>
            <div className="flex-1 flex gap-2 relative">
              <textarea
                ref={replyInputRef}
                value={replyText}
                onChange={(e) => {
                  setReplyText(e.target.value);
                  detectMention(e.target.value, replyInputRef.current, setReplyMentionQuery, setReplyMentionPos);
                }}
                onKeyDown={(e) => {
                  if (replyMentionQuery !== null) return;
                  handleKeyDown(e);
                }}
                placeholder={t('dashboardMain.replies.placeholder', "Write a reply... Type @ to mention")}
                rows={1}
                className="flex-1 bg-white/4 border border-white/6 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#4e4e60] focus:outline-hidden focus:border-iris-500/40 resize-none min-h-[36px] max-h-[120px] transition-colors"
                style={{ height: 'auto' }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = Math.min(target.scrollHeight, 120) + 'px';
                }}
              />
              {replyMentionQuery !== null && (
                <MentionDropdown
                  query={replyMentionQuery}
                  position={replyMentionPos}
                  onSelect={(user) => handleMentionSelect(user, replyText, setReplyText, replyInputRef.current, replyMentions, setReplyMentions, setReplyMentionQuery)}
                  onClose={() => setReplyMentionQuery(null)}
                />
              )}
              <button
                onClick={handleSendReply}
                disabled={sendingReply || !replyText.trim()}
                className="self-end p-2 rounded-lg bg-iris-600 text-[#0C0C12] hover:bg-iris-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                {sendingReply ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
          </div>

          {/* Threaded replies */}
          {loadingReplies ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 size={16} className="animate-spin text-[#9a9aac]" />
            </div>
          ) : replies.length > 0 ? (
            <div className="px-2 pb-3">
              <ThreadedComments
                scoopId={scoop.id}
                replies={replies}
                onReplyAdded={() => fetchReplies()}
              />
            </div>
          ) : (
            <div className="px-3 pb-3">
              <p className="text-center text-xs text-[#4e4e60] py-2">{t('dashboardMain.replies.noReplies', "No replies yet — be the first to respond")}</p>
            </div>
          )}
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-xs" onClick={() => setShowReportModal(false)} />
          <div className="relative w-full max-w-md bg-[#16161e] border border-white/10 rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 flex-wrap gap-y-2">
              <div className="flex items-center gap-2">
                <Flag size={18} className="text-red-400" />
                <h3 className="text-base font-semibold text-white">{t('dashboardMain.reportModal.title', "Report Content")}</h3>
              </div>
              <button
                onClick={() => setShowReportModal(false)}
                className="p-1.5 rounded-lg text-[#9a9aac] hover:text-white hover:bg-white/5 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {reportSubmitted ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-500/15 flex items-center justify-center">
                  <Check size={24} className="text-green-400" />
                </div>
                <p className="text-sm text-white font-medium mb-1">{t('dashboardMain.reportModal.thankYou', "Thank you for reporting")}</p>
                <p className="text-xs text-[#9a9aac]">{t('dashboardMain.reportModal.reviewMessage', "We'll review this content and take appropriate action.")}</p>
              </div>
            ) : (
              <>
                {/* Body */}
                <div className="p-5">
                  <p className="text-xs text-[#9a9aac] mb-4">
                    {t('dashboardMain.reportModal.helpText', "Help us understand what's wrong with this content. Your report is anonymous.")}
                  </p>

                  {/* Category selection */}
                  <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
                    {REPORT_CATEGORIES.map(cat => (
                      <label
                        key={cat.code}
                        className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                          reportCategory === cat.code
                            ? 'bg-red-500/10 border border-red-500/30'
                            : 'bg-white/2 border border-white/5 hover:border-white/10'
                        }`}
                      >
                        <input
                          type="radio"
                          name="reportCategory"
                          value={cat.code}
                          checked={reportCategory === cat.code}
                          onChange={(e) => setReportCategory(e.target.value as ReportCategory)}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          reportCategory === cat.code ? 'border-red-400' : 'border-[#4e4e60]'
                        }`}>
                          {reportCategory === cat.code && (
                            <div className="w-2 h-2 rounded-full bg-red-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${reportCategory === cat.code ? 'text-white' : 'text-[#b0b0c0]'}`}>
                            {t(`reportCategories.${cat.labelKey}`)}
                          </p>
                          <p className="text-[13px] text-[#9a9aac] mt-0.5">{t(`reportCategories.${cat.descKey}`)}</p>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Additional details */}
                  {reportCategory && (
                    <div className="mt-4">
                      <label className="block text-xs text-[#9a9aac] mb-1.5">{t('dashboardMain.reportModal.additionalDetails', "Additional details (optional)")}</label>
                      <textarea
                        value={reportDescription}
                        onChange={(e) => setReportDescription(e.target.value.slice(0, 500))}
                        placeholder={t('dashboardMain.reportModal.additionalPlaceholder', "Provide any additional context...")}
                        rows={3}
                        className="w-full px-3 py-2 bg-white/4 border border-white/6 rounded-lg text-sm text-white placeholder:text-[#4e4e60] focus:outline-hidden focus:border-red-500/40 resize-none transition-colors"
                      />
                      <p className="text-[12px] text-[#4e4e60] mt-1 text-right">{reportDescription.length}/500</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-white/5 bg-white/1 flex-wrap">
                  <div className="flex items-center gap-1.5 text-[12px] text-[#9a9aac]">
                    <AlertTriangle size={12} />
                    <span>{t('dashboardMain.reportModal.falseReportsWarning', "False reports may result in account action")}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowReportModal(false)}
                      className="px-4 py-2 rounded-lg text-xs text-[#a0a0b0] hover:text-white hover:bg-white/5 transition-colors"
                    >
                      {t('dashboardMain.reportModal.cancel', "Cancel")}
                    </button>
                    <button
                      onClick={handleSubmitReport}
                      disabled={!reportCategory || submittingReport}
                      className="px-4 py-2 rounded-lg text-xs font-medium text-white bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                    >
                      {submittingReport ? (
                        <>
                          <Loader2 size={12} className="animate-spin" />
                          {t('dashboardMain.reportModal.submitting', "Submitting...")}
                        </>
                      ) : (
                        t('dashboardMain.reportModal.submitReport', "Submit Report")
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {uploadError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-xs" onClick={() => setUploadError(null)} />
          <div className="relative w-full max-w-sm bg-[#16161e] border border-white/10 rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-500/15 flex items-center justify-center">
                <ShieldAlert size={24} className="text-red-400" />
              </div>
              <h3 className="text-sm font-semibold text-white mb-2">{t('dashboardMain.errors.contentPolicyViolation', "Content policy violation")}</h3>
              <p className="text-xs text-[#8e8ea0] mb-5">{uploadError}</p>
              <button
                onClick={() => setUploadError(null)}
                className="px-5 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
